/**
 * ProsPix PXL runtime entrypoint.
 * - Resolves config, consent, and IDs
 * - Drains loader queue safely
 * - Loads plugins and flushes events in the background
 */
import manifestFallback from "../config/manifest.json";
import { readAttributionSnapshot } from "../lib/attribution";
import { createCompanyVisitCapture } from "../lib/company-visit";
import { readStoredConsent, resolveConsent } from "../lib/consent";
import {
  acknowledgeConsentEvidenceDelivery,
  buildConsentEvidence,
  commitConsentEvidenceToDurableOutbox,
  isConsentEvidenceSnapshotCurrent,
  resolveCurrentConsentEvidenceSnapshot,
  type ConsentEvidenceOutboxMarker,
} from "../lib/consent-evidence";
import { recordConsentResponse } from "../lib/consent-response";
import { buildContext } from "../lib/context";
import {
  groupEventsByDeliveryContext,
  isExplicitDenialQueuedEvent,
  isAnonymousConsentResponseQueuedEvent,
  isPrivacyEssentialQueuedEvent,
  isConsentBoundDeliveryCurrent,
  queuedFirstPartyCookieTarget,
  queuedFirstPartyCookieTargetKey,
  retainOnlyEssentialQueuedEvents,
  resolvePluginTransportKind,
  settleDeliveryAttempt,
  shouldWithholdForPendingCookieRevoke,
  stageDeliveryAttempt,
} from "../lib/delivery";
import {
  prepareEnquiryAttribution,
  startEnquiryCapture,
} from "../lib/enquiries";
import { createEventBus } from "../lib/event-bus";
import {
  isAnonymousAnalyticsFallbackEvent,
  isAnonymousAnalyticsFallbackPlugin,
  resolveAnonymousAnalyticsFallbackPolicy,
  resolveEventDeliveryClass,
  shouldUseAnonymousAnalyticsFallback,
  type EventDeliveryClass,
} from "../lib/event-privacy";
import {
  acknowledgeFirstPartyConsentRevision,
  buildFirstPartyCookieExpireRequest,
  buildFirstPartyCookieIssueRequest,
  buildFirstPartyCookieProbeRequest,
  buildFirstPartyCookieRegrantRequest,
  buildFirstPartyCookieRevokeRequest,
  clearFirstPartyConsentTransitionToken,
  classifyFirstPartyCookieProbeResult,
  createFirstPartyCookieRevocationCoordinator,
  createFirstPartyConsentTransitionToken,
  firstPartyConsentTransitionTokenStorageKey,
  firstPartyCookieRevocationStorageKey,
  hasFirstPartyConsentFenceSignals,
  hasFirstPartyDeliveryBroadening,
  hasFirstPartyPurposeBroadening,
  hasFullFirstPartyPurposeGrant,
  isFreshFirstPartyConsentTransition,
  isFirstPartyCookieProbeResultCurrent,
  isFirstPartyCookiePluginEnabled,
  issueFirstPartyCookie,
  MAX_FIRST_PARTY_CONSENT_REVISION_HISTORY,
  persistFirstPartyConsentRevision,
  readFirstPartyConsentRevisions,
  regrantFirstPartyConsent,
  resolveFirstPartyConsentPurposeDecisions,
  resolveFirstPartyConsentRevisionForDelivery,
  resolveFirstPartyIdentityPolicy,
  shouldHoldFirstPartyConsentFence,
  shouldRevokeFirstPartyCookie,
  type FirstPartyConsentPurpose,
  type FirstPartyConsentPurposeDecisions,
  type FirstPartyConsentRevision,
  type FirstPartyConsentTransition,
} from "../lib/firstparty";
import {
  isProtectedIdentityTraitKey,
  readIdentityRecordValue,
} from "../lib/identity-fields";
import { resolveIdentityFormPolicyBlockReason } from "../lib/identity-form-policy";
import { createIdManager } from "../lib/ids";
import {
  normalizeDecision,
  normalizeExposure,
  normalizeIdentityResolution,
  normalizeOutcome,
  type DecisionInput,
  type ExposureInput,
  type IdentityResolutionInput,
  type OutcomeInput,
} from "../lib/intelligence-api";
import { createPageLifecycleEventGate } from "../lib/lifecycle";
import { createLogger } from "../lib/logger";
import {
  clearDurableOptout,
  hasDurableOptout,
  markDurableOptout,
  resolveQueuedInitOverride,
  resolveQueuedPrivacyBootstrap,
  type QueuedConsentPayload,
} from "../lib/optout";
import { canAcceptProvider, getPlatformStatus } from "../lib/platform";
import { createPluginActivityFence } from "../lib/plugin-activity";
import {
  createQueue,
  type QueuedDeliveryContext,
  type QueuedEvent,
} from "../lib/queue";
import { createRedactor } from "../lib/redact";
import {
  buildStorageFailureConsentSnapshot,
  isConsentDeliveryAllowed,
  normalizeSharedConsentSnapshot,
  persistSharedConsentRecord,
  requiresConsentRevocationBinding,
  resolveConsentSnapshotForSharedWrite,
  resolveConsentLifecycleActions,
  resolveConsentStorageScope,
  resolveSharedConsentSynchronization,
  resolveSharedConsentStorageState,
  sharedConsentSnapshotsEqual,
  shouldHoldConsentPersistenceLatch,
} from "../lib/shared-consent";
import { createStorageAdapters } from "../lib/storage";
import {
  isActiveTestEnrollment,
  isRestoredHostChoiceDuringTest,
  mountTestEnrollmentPrompt,
  takeTestInvitation,
  TEST_ENROLLMENT_SOURCE,
  updateTestCaptureStatus,
  type TestCaptureStatus,
} from "../lib/test-enrollment";
import { postJson, postWithRetry, postWithRetryResult } from "../lib/transport";
import type {
  ConsentSnapshot,
  DataCategory,
  FirstPartyIdentityCapture,
  PixelConfig,
  PixelFlags,
  PluginContext,
  PluginFactory,
  PluginInstance,
  PluginManifest,
  TrackEvent,
} from "../lib/types";
import { ulid } from "../lib/ulid";
import { now, safeDocument, safeWindow } from "../lib/utils";

import {
  deepMerge,
  loadDefaults,
  loadFlags,
  loadJson,
  loadRemoteConfig,
  resolveConfig,
} from "./config";

const GLOBAL_GUARD = "__PROSPIX_PIXEL_V2__";
const LEGACY_GLOBAL_GUARD = "__PROSPIX_PIXEL_V1__";
const PLUGIN_REGISTER = "__PROSPIX_PLUGIN_REGISTER__";

function findSelfScript(): HTMLScriptElement | null {
  if (typeof document === "undefined") return null;
  if (
    document.currentScript &&
    document.currentScript instanceof HTMLScriptElement
  ) {
    return document.currentScript;
  }
  const scripts = document.getElementsByTagName("script");
  for (let i = scripts.length - 1; i >= 0; i -= 1) {
    const src = scripts[i].src || "";
    if (src.includes("/cdn/pxl-loader.js") || src.includes("/cdn/v2/pxl.js")) {
      return scripts[i];
    }
  }
  return scripts.length > 0 ? scripts[scripts.length - 1] : null;
}

function resolveScriptUrl(): URL {
  const script = findSelfScript();
  if (script?.src) return new URL(script.src, window.location.href);
  return new URL(window.location.href);
}

function loadManifest(scriptUrl: URL): Promise<PluginManifest> {
  const fallback = manifestFallback as PluginManifest;
  const url = new URL(
    "/cdn/v2/manifests/plugin-manifest.json",
    scriptUrl.origin,
  );
  return loadJson(url.toString(), fallback);
}

function applyFeatureFlags(
  config: PixelConfig,
  features: Record<string, boolean>,
): PixelConfig {
  if (!features || !Object.keys(features).length) return config;
  const disabled = new Set(config.plugins.disabled ?? []);
  const disable = (name?: string) => {
    if (name) disabled.add(name);
  };
  const isFalse = (key: string) => features[key] === false;

  if (isFalse("engagement")) disable("engagement");
  if (isFalse("scrollDepth")) disable("scroll");
  if (isFalse("downloads")) disable("download");
  if (isFalse("forms")) disable("forms");
  if (isFalse("ecommerce")) disable("ecommerce");
  if (isFalse("video")) disable("video");
  if (isFalse("heatmap")) disable("heatmap");
  if (isFalse("replay")) disable("replay");
  if (isFalse("webVitals")) disable("webvitals");
  if (isFalse("performance")) disable("perf");
  if (isFalse("errors")) disable("errors");
  if (isFalse("bots")) disable("bot");
  if (isFalse("adblock")) disable("adblock");
  if (isFalse("sessionQuality")) disable("session_quality");
  if (isFalse("experiments")) disable("experiments");
  if (isFalse("identityLinkage")) disable("identitylink");
  if (isFalse("identityLinkage")) disable("identitystorage");
  if (isFalse("intentScoring")) disable("intent");
  if (isFalse("journeyAbandonment")) disable("journey");
  if (isFalse("search")) disable("search");
  if (isFalse("impressions")) disable("impressions");
  if (isFalse("firstPartyCookies")) disable("firstparty");
  if (isFalse("pageView")) disable("router");

  const clicksDisabled = isFalse("clicks");
  const ctasDisabled = isFalse("ctas");
  if (clicksDisabled && ctasDisabled) disable("click");

  return {
    ...config,
    features,
    plugins: {
      ...config.plugins,
      disabled: Array.from(disabled),
    },
  };
}

function normalizeConsentMode(mode?: string): PixelConfig["consentMode"] {
  if (!mode) return undefined;
  if (mode === "opt-out" || mode === "opt-in" || mode === "strict") return mode;
  return undefined;
}

const DATA_CATEGORY_KINDS: Record<
  DataCategory,
  "essential" | "analytics" | "marketing"
> = {
  context_page: "analytics",
  context_device: "analytics",
  marketing_params: "marketing",
  network_hints: "analytics",
  behavioral_events: "analytics",
  form_analytics: "analytics",
  commerce_analytics: "marketing",
  video_analytics: "analytics",
  performance_analytics: "analytics",
  error_telemetry: "essential",
  bot_signals: "essential",
  attribution: "marketing",
  outbound_clicks: "marketing",
  session_quality: "analytics",
  search_intent: "marketing",
  impression_analytics: "analytics",
  frustration_signals: "analytics",
  experiment_exposure: "analytics",
  identity_linkage: "marketing",
  identity_resolution: "marketing",
  audience_decisioning: "marketing",
  heatmap: "analytics",
  replay: "analytics",
  adblock_signals: "analytics",
  geo_enrichment: "analytics",
  third_party_destinations: "marketing",
  consent_analytics: "essential",
};

const DATA_CATEGORIES = Object.keys(DATA_CATEGORY_KINDS) as DataCategory[];

(function bootstrap() {
  try {
    const win = safeWindow();
    if (!win) return;
    if ((win as any)[GLOBAL_GUARD] || (win as any)[LEGACY_GLOBAL_GUARD]) {
      return;
    }
    (win as any)[GLOBAL_GUARD] = true;
    // Claim the v1 guard too. If a host accidentally includes both immutable
    // generations, neither runtime may initialize after the first one wins.
    (win as any)[LEGACY_GLOBAL_GUARD] = true;

    const scriptUrl = resolveScriptUrl();
    const storage = createStorageAdapters();

    let config = resolveConfig(scriptUrl);
    config.consentMode = normalizeConsentMode(config.consentMode);

    const logger = createLogger(config.debug);

    let consentKey: string | undefined;
    let consentEvidenceKey: string | undefined;
    let consentRevocationKey: string | undefined;
    let boundConsentSiteKey: string | undefined;
    let lastObservedSharedConsentRaw: string | null = null;
    let lastObservedSharedRevocationRaw: string | null = null;
    const readConsentFromStorage = () =>
      consentKey ? readStoredConsent(storage.local.get(consentKey)) : null;
    const readSharedConsentStorageState = () =>
      resolveSharedConsentStorageState(
        consentKey ? storage.local.get(consentKey) : null,
        consentRevocationKey ? storage.local.get(consentRevocationKey) : null,
      );
    const readSharedConsentRecordFromStorage = () =>
      readSharedConsentStorageState().record;
    const readSharedConsentFromStorage = () =>
      readSharedConsentRecordFromStorage()?.snapshot ?? null;
    const readSharedRevocationTokenFromStorage = () =>
      readSharedConsentStorageState().revocationToken;

    let sharedConsentPersistenceFailed = false;
    let consentSnapshot: ConsentSnapshot = {
      state: "unknown",
      gpc: false,
      dnt: false,
    };
    let currentConsentEvidenceMarker: ConsentEvidenceOutboxMarker | undefined;
    const bindConsentStorageScope = (siteKey: string | undefined) => {
      const scope = resolveConsentStorageScope(siteKey);
      const normalizedSiteKey = siteKey?.trim() || undefined;
      if (normalizedSiteKey === boundConsentSiteKey) return;
      boundConsentSiteKey = normalizedSiteKey;
      consentKey = scope?.consentKey;
      consentEvidenceKey = scope?.evidenceKey;
      consentRevocationKey = scope?.revocationKey;
      if (!scope) {
        sharedConsentPersistenceFailed = false;
        consentSnapshot = { state: "unknown", gpc: false, dnt: false };
        lastObservedSharedConsentRaw = null;
        lastObservedSharedRevocationRaw = null;
        return;
      }
      lastObservedSharedConsentRaw = storage.local.get(scope.consentKey);
      lastObservedSharedRevocationRaw = storage.local.get(scope.revocationKey);
      const sharedState = readSharedConsentStorageState();
      sharedConsentPersistenceFailed = sharedState.fenceMismatch;
      consentSnapshot = sharedState.fenceMismatch
        ? { state: "denied", gpc: false, dnt: false }
        : (readSharedConsentFromStorage() ?? {
            state:
              readConsentFromStorage()?.state ?? config.privacy.defaultConsent,
            gpc: false,
            dnt: false,
          });
    };
    bindConsentStorageScope(config.siteKey);
    let analyticsRevocationGeneration = 0;
    let marketingRevocationGeneration = 0;
    let identityRevocationGeneration = 0;
    let consentTransitionGeneration = 0;
    let enquiryAttributionGeneration = 0;
    const volatileFirstPartyTransitionRevisions = new Map<
      string,
      FirstPartyConsentRevision[]
    >();
    const verifiedFirstPartyTransportTargets = new Set<string>();
    const unresolvedFirstPartyConsentPurposes = new Map<
      string,
      Set<FirstPartyConsentPurpose>
    >();
    const blockedFirstPartyTransitionTargets = new Map<string, number>();
    const sharedGrantProbePendingTargets = new Set<string>();
    const firstPartyConsentFenceGenerations = new Map<string, number>();
    const firstPartyProbeAttempts = new Map<
      string,
      { generation: number; promise: Promise<boolean> }
    >();

    let flags: PixelFlags = {};
    let manifest: PluginManifest = manifestFallback as PluginManifest;

    const bus = createEventBus<{
      track: TrackEvent;
      consent: ConsentSnapshot;
    }>();

    let redactor = createRedactor({
      redactKeys: config.privacy.redactKeys,
      allowedQueryParams: config.privacy.allowedQueryParams,
    });

    let testInvitation = takeTestInvitation(window);
    const idManager = createIdManager(storage.local, storage.cookies, {
      sessionTtlMs: 30 * 60 * 1000,
    });

    let queue = createQueue(storage.local, {
      key: `prospix_queue_${config.siteKey || "default"}`,
      maxEvents: config.queue.maxEvents,
      maxBytes: config.queue.maxBytes,
      ttlMs: config.queue.ttlMs,
    });
    let privacyBootstrapPending = false;
    let testCaptureStatus: TestCaptureStatus = "checking";
    const observeTestCaptureResponse = (
      status: number | undefined,
      token: string | undefined,
    ) => {
      if (
        !token ||
        !isActiveTestEnrollment(consentSnapshot) ||
        consentSnapshot.cmp?.testEnrollmentToken !== token
      )
        return;
      if (status === 403) testCaptureStatus = "blocked";
      else if (status && status >= 200 && status < 300)
        testCaptureStatus = "active";
      else return;
      updateTestCaptureStatus(
        testCaptureStatus,
        consentSnapshot.cmp!.testEnrollmentExpiresAt!,
      );
    };
    const pageLifecycleEventGate = createPageLifecycleEventGate();

    const pendingPluginLoads = new Map<
      string,
      { promise: Promise<void>; resolve: () => void }
    >();
    const pluginFactories = new Map<string, PluginFactory>();
    const pluginInstances = new Map<string, PluginInstance>();
    let lastPageView:
      | {
          observation: NonNullable<PluginContext["lastPageView"]>;
          siteKey: string;
          revocationToken: string | null;
        }
      | undefined;
    const pluginInstanceDeactivators = new WeakMap<
      PluginInstance,
      () => void
    >();

    const clearBrowserIds = () => {
      idManager.clear();
      lastPageView = undefined;
      for (const key of storage.local.keys("prospix_last_visit_v2_")) {
        storage.local.remove(key);
      }
    };

    (win as any)[PLUGIN_REGISTER] = (name: string, factory: PluginFactory) => {
      pluginFactories.set(name, factory);
      const pending = pendingPluginLoads.get(name);
      if (pending) {
        pending.resolve();
        pendingPluginLoads.delete(name);
      }
    };

    const getIds = () => idManager.getIds();

    const firstPartyTargetKey = (target: {
      apiBase: string;
      siteKey: string;
    }) => firstPartyConsentTransitionTokenStorageKey(target);
    const currentFirstPartyTarget = () => ({
      apiBase: config.apiBase,
      siteKey: config.siteKey,
    });
    const isFirstPartyTransportVerified = (
      target = currentFirstPartyTarget(),
    ) => verifiedFirstPartyTransportTargets.has(firstPartyTargetKey(target));
    const firstPartyConsentFenceGeneration = (
      target = currentFirstPartyTarget(),
    ) =>
      firstPartyConsentFenceGenerations.get(firstPartyTargetKey(target)) ?? 0;
    const advanceFirstPartyConsentFenceGeneration = (
      target = currentFirstPartyTarget(),
    ) => {
      const key = firstPartyTargetKey(target);
      const generation = firstPartyConsentFenceGeneration(target) + 1;
      firstPartyConsentFenceGenerations.set(key, generation);
      return generation;
    };
    const hasUnacknowledgedFirstPartyConsentRestriction = (
      target = currentFirstPartyTarget(),
    ) => {
      const key = firstPartyTargetKey(target);
      return (
        (volatileFirstPartyTransitionRevisions.get(key)?.length ?? 0) > 0 ||
        readFirstPartyConsentRevisions(storage.local, target).length > 0 ||
        blockedFirstPartyTransitionTargets.has(key)
      );
    };
    const hasLocalFirstPartyConsentFence = (
      target = currentFirstPartyTarget(),
    ) => {
      const key = firstPartyTargetKey(target);
      return (
        sharedGrantProbePendingTargets.has(key) ||
        hasFirstPartyConsentFenceSignals({
          serverFenced: false,
          revokePending:
            storage.local.get(firstPartyCookieRevocationStorageKey(target)) ===
            "1",
          transitionHistory:
            hasUnacknowledgedFirstPartyConsentRestriction(target),
          transitionBlocked: blockedFirstPartyTransitionTargets.has(key),
        })
      );
    };
    const isFirstPartyPurposeFenced = (
      purpose: FirstPartyConsentPurpose,
      target = currentFirstPartyTarget(),
    ) =>
      hasLocalFirstPartyConsentFence(target) ||
      unresolvedFirstPartyConsentPurposes
        .get(firstPartyTargetKey(target))
        ?.has(purpose) === true;
    const isFirstPartyProbeConfirmed = (target = currentFirstPartyTarget()) =>
      isFirstPartyTransportVerified(target) &&
      (isActiveTestEnrollment(consentSnapshot) ||
        !isFirstPartyPurposeFenced("marketing", target)) &&
      !isFirstPartyPurposeFenced("identity_resolution", target) &&
      !isFirstPartyPurposeFenced("enrichment", target);
    const markFirstPartyUnresolvedPurposes = (
      target: { apiBase: string; siteKey: string },
      purposes: FirstPartyConsentPurposeDecisions,
    ) => {
      advanceFirstPartyConsentFenceGeneration(target);
      const unresolved = new Set<FirstPartyConsentPurpose>();
      for (const purpose of [
        "analytics",
        "marketing",
        "identity_resolution",
        "enrichment",
      ] as const) {
        if (!purposes[purpose]) unresolved.add(purpose);
      }
      const key = firstPartyTargetKey(target);
      if (unresolved.size > 0) {
        unresolvedFirstPartyConsentPurposes.set(key, unresolved);
      } else {
        unresolvedFirstPartyConsentPurposes.delete(key);
      }
    };
    const applyFirstPartyPurposeFence = (
      snapshot: ConsentSnapshot,
      target = currentFirstPartyTarget(),
    ): ConsentSnapshot => {
      // Pending local acknowledgements stop delivery via canTrack; they are
      // not authoritative evidence that the user denied every purpose.
      const unresolved = unresolvedFirstPartyConsentPurposes.get(
        firstPartyTargetKey(target),
      );
      const analyticsFenced = unresolved?.has("analytics") === true;
      const marketingFenced =
        (!isActiveTestEnrollment(snapshot) &&
          unresolved?.has("marketing") === true) ||
        unresolved?.has("identity_resolution") === true ||
        unresolved?.has("enrichment") === true;
      if (!analyticsFenced && !marketingFenced) return snapshot;
      if (analyticsFenced) {
        return { state: "denied", gpc: snapshot.gpc, dnt: snapshot.dnt };
      }
      return {
        ...snapshot,
        state: "granted",
        cmp: {
          ...snapshot.cmp,
          source: "prospix_ui",
          purposes: { version: 1, analytics: true, marketing: false },
        },
      };
    };

    // Central consent gate used by runtime and plugins.
    const resolveConsentMode = () => {
      const region = config.privacy.region;
      const regional = region
        ? config.privacy.regionConsentModes?.[region]
        : undefined;
      return normalizeConsentMode(regional ?? config.consentMode);
    };

    const canTrackWithSnapshot = (
      snapshot: ConsentSnapshot,
      kind: "essential" | "marketing" | "analytics" = "analytics",
    ) => {
      if (!config.siteKey) return false;
      if (flags.killSwitch) return kind === "essential";
      if (privacyBootstrapPending || hasDurableOptout(storage.local)) {
        return kind === "essential";
      }
      if (kind === "analytics" && isFirstPartyPurposeFenced("analytics")) {
        return false;
      }
      // Marketing/identity can rely on browser cookies only after a dedicated
      // credentialed probe roundtrip. Loading public config is not proof that
      // the browser accepted the partitioned HttpOnly cookie.
      if (kind === "marketing" && !isFirstPartyProbeConfirmed()) return false;
      return isConsentDeliveryAllowed({
        snapshot,
        kind,
        consentMode: resolveConsentMode(),
        respectGpc: config.privacy.respectGpc,
        respectDnt: config.privacy.respectDnt,
      });
    };
    const canTrack = (
      kind: "essential" | "marketing" | "analytics" = "analytics",
    ) => canTrackWithSnapshot(consentSnapshot, kind);

    const isConsentAuthorizedForSnapshot = (
      snapshot: ConsentSnapshot,
      kind: "analytics" | "marketing",
    ) => {
      if (
        !config.siteKey ||
        flags.killSwitch ||
        hasDurableOptout(storage.local)
      ) {
        return false;
      }
      return isConsentDeliveryAllowed({
        snapshot,
        kind,
        consentMode: resolveConsentMode(),
        respectGpc: config.privacy.respectGpc,
        respectDnt: config.privacy.respectDnt,
      });
    };

    const readFirstPartyTransitionRevisions = (target: {
      apiBase: string;
      siteKey: string;
    }) => {
      const revisions = new Map<string, FirstPartyConsentRevision>();
      for (const revision of [
        ...(volatileFirstPartyTransitionRevisions.get(
          firstPartyTargetKey(target),
        ) ?? []),
        ...readFirstPartyConsentRevisions(storage.local, target),
      ]) {
        const existing = revisions.get(revision.transitionToken);
        if (!existing || revision.occurredAt >= existing.occurredAt) {
          revisions.set(revision.transitionToken, revision);
        }
      }
      return Array.from(revisions.values()).sort(
        (left, right) =>
          right.occurredAt - left.occurredAt ||
          right.transitionToken.localeCompare(left.transitionToken),
      );
    };
    const readFirstPartyTransitionRevision = (target: {
      apiBase: string;
      siteKey: string;
    }) => readFirstPartyTransitionRevisions(target)[0] ?? null;
    const ensureFirstPartyTransitionRevision = (
      target: { apiBase: string; siteKey: string },
      rotate = false,
      occurredAt = now(),
    ) => {
      const existing = readFirstPartyTransitionRevision(target);
      if (!rotate && existing) return existing;
      const history = readFirstPartyTransitionRevisions(target);
      if (
        rotate &&
        history.length >= MAX_FIRST_PARTY_CONSENT_REVISION_HISTORY
      ) {
        blockedFirstPartyTransitionTargets.set(
          firstPartyTargetKey(target),
          occurredAt,
        );
        logger.warn("firstparty_transition_history_full");
        return null;
      }
      const transitionToken =
        createFirstPartyConsentTransitionToken() ?? undefined;
      if (!transitionToken) {
        blockedFirstPartyTransitionTargets.set(
          firstPartyTargetKey(target),
          occurredAt,
        );
        logger.warn("firstparty_transition_token_unavailable");
        return null;
      }
      const revision = { transitionToken, occurredAt };
      const nextHistory = [
        revision,
        ...history.filter(
          (entry) => entry.transitionToken !== revision.transitionToken,
        ),
      ];
      volatileFirstPartyTransitionRevisions.set(
        firstPartyTargetKey(target),
        nextHistory,
      );
      if (!persistFirstPartyConsentRevision(storage.local, target, revision)) {
        logger.warn("firstparty_transition_token_not_persisted");
      }
      blockedFirstPartyTransitionTargets.delete(firstPartyTargetKey(target));
      return revision;
    };
    const ensureFirstPartyTransitionToken = (
      target: { apiBase: string; siteKey: string },
      rotate = false,
      occurredAt = now(),
    ) =>
      ensureFirstPartyTransitionRevision(target, rotate, occurredAt)
        ?.transitionToken ?? null;
    const clearFirstPartyTransitionToken = (target: {
      apiBase: string;
      siteKey: string;
    }) => {
      volatileFirstPartyTransitionRevisions.delete(firstPartyTargetKey(target));
      blockedFirstPartyTransitionTargets.delete(firstPartyTargetKey(target));
      clearFirstPartyConsentTransitionToken(storage.local, target);
    };
    const acknowledgeFirstPartyTransitionRevision = (
      target: { apiBase: string; siteKey: string },
      transitionToken: string,
    ) => {
      const key = firstPartyTargetKey(target);
      const remainingVolatile = (
        volatileFirstPartyTransitionRevisions.get(key) ?? []
      ).filter((revision) => revision.transitionToken !== transitionToken);
      if (remainingVolatile.length > 0) {
        volatileFirstPartyTransitionRevisions.set(key, remainingVolatile);
      } else {
        volatileFirstPartyTransitionRevisions.delete(key);
      }
      const acknowledged = acknowledgeFirstPartyConsentRevision(
        storage.local,
        target,
        transitionToken,
      );
      if (acknowledged) advanceFirstPartyConsentFenceGeneration(target);
      return acknowledged;
    };
    const isCurrentFirstPartyTarget = (target: {
      apiBase: string;
      siteKey: string;
    }) =>
      queuedFirstPartyCookieTargetKey(target) ===
      queuedFirstPartyCookieTargetKey({
        apiBase: config.apiBase,
        siteKey: config.siteKey,
      });
    const expireFirstPartyTarget = async (target: {
      apiBase: string;
      siteKey: string;
    }) => {
      const request = buildFirstPartyCookieExpireRequest(target.siteKey);
      if (!request) return false;
      const url = `${target.apiBase.replace(/\/+$/, "")}/pxl/firstparty`;
      const result = await postWithRetryResult(url, request);
      if (!result.ok) {
        logger.warn("firstparty_expire_unconfirmed", {
          status: result.status,
        });
      }
      return result.ok;
    };
    const ensureFirstPartyCookieTransportProbe = (
      target: {
        apiBase: string;
        siteKey: string;
      },
      force = false,
    ): Promise<boolean> => {
      const key = firstPartyTargetKey(target);
      if (!force && verifiedFirstPartyTransportTargets.has(key)) {
        return Promise.resolve(true);
      }
      const generation = firstPartyConsentFenceGeneration(target);
      const existing = firstPartyProbeAttempts.get(key);
      if (existing) {
        if (!force || existing.generation === generation) {
          return existing.promise;
        }
        return existing.promise.then(() =>
          ensureFirstPartyCookieTransportProbe(target, true),
        );
      }
      const request = buildFirstPartyCookieProbeRequest(target.siteKey);
      if (!request) return Promise.resolve(false);
      const url = `${target.apiBase.replace(/\/+$/, "")}/pxl/firstparty`;
      const attempt = postWithRetryResult(url, request, {
        captureJsonResponse: true,
        beforeAttempt: () =>
          queuedFirstPartyCookieTargetKey(target) ===
          queuedFirstPartyCookieTargetKey(currentFirstPartyTarget()),
      })
        .then((result) => {
          const probe = classifyFirstPartyCookieProbeResult(result);
          const resultIsCurrent = isFirstPartyCookieProbeResultCurrent({
            capturedGeneration: generation,
            currentGeneration: firstPartyConsentFenceGeneration(target),
            targetCurrent:
              queuedFirstPartyCookieTargetKey(target) ===
              queuedFirstPartyCookieTargetKey(currentFirstPartyTarget()),
          });
          if (!resultIsCurrent) return false;
          if (probe.state === "unverified") {
            // A forced refresh invalidates an older cached 204. Until a later
            // probe succeeds, only the identifier-free aggregate path may run.
            verifiedFirstPartyTransportTargets.delete(key);
            return false;
          }
          verifiedFirstPartyTransportTargets.add(key);
          if (probe.state === "verified_fenced") {
            unresolvedFirstPartyConsentPurposes.set(
              key,
              new Set(probe.unresolvedPurposes),
            );
          } else if (
            probe.state === "verified_clear" &&
            !hasUnacknowledgedFirstPartyConsentRestriction(target)
          ) {
            unresolvedFirstPartyConsentPurposes.delete(key);
          } else if (probe.state === "verified_clear") {
            return false;
          }
          return true;
        })
        .catch(() => false)
        .finally(() => {
          if (firstPartyProbeAttempts.get(key)?.promise === attempt) {
            firstPartyProbeAttempts.delete(key);
          }
        });
      firstPartyProbeAttempts.set(key, { generation, promise: attempt });
      return attempt;
    };
    const revokeFirstPartyTarget = async (target: {
      apiBase: string;
      siteKey: string;
    }) => {
      if (!target.siteKey) return false;
      // A retired origin has no safe current purpose snapshot. Expire its
      // cookie without creating or broadening a consent receipt there.
      if (!isCurrentFirstPartyTarget(target)) {
        return expireFirstPartyTarget(target);
      }
      const targetKey = firstPartyTargetKey(target);
      const blockedTransitionAt =
        blockedFirstPartyTransitionTargets.get(targetKey);
      if (blockedTransitionAt !== undefined) {
        if (
          readFirstPartyTransitionRevisions(target).length >=
          MAX_FIRST_PARTY_CONSENT_REVISION_HISTORY
        ) {
          return expireFirstPartyTarget(target);
        }
        // An older exact revision was acknowledged while the newest
        // restriction waited at the bounded-history cap. Allocate a fresh
        // capability for that blocked transition; never recycle an old token.
        blockedFirstPartyTransitionTargets.delete(targetKey);
        if (
          !ensureFirstPartyTransitionRevision(target, true, blockedTransitionAt)
        ) {
          return expireFirstPartyTarget(target);
        }
      }
      const policy = buildFirstPartyPolicySnapshot(consentSnapshot);
      if (
        resolveFirstPartyPolicy().createBrowserIds &&
        !policy.firstPartyCookies
      ) {
        return expireFirstPartyTarget(target);
      }
      const revision = ensureFirstPartyTransitionRevision(target);
      const request = revision
        ? buildFirstPartyCookieRevokeRequest({
            siteKey: target.siteKey,
            transitionToken: revision.transitionToken,
            occurredAt: revision.occurredAt,
            consent: consentSnapshot,
            policy,
          })
        : null;
      // Secure token generation can fail only closed: still expire the cookie,
      // while durable consent evidence keeps the server purpose ledger denied.
      if (!request) return expireFirstPartyTarget(target);
      markFirstPartyUnresolvedPurposes(
        target,
        resolveFirstPartyPurposeSnapshot(consentSnapshot),
      );
      const url = `${target.apiBase.replace(/\/+$/, "")}/pxl/firstparty`;
      const result = await postWithRetryResult(url, request);
      if (result.ok) {
        acknowledgeFirstPartyTransitionRevision(
          target,
          request.transitionToken,
        );
      }
      if (!result.ok) {
        logger.warn("firstparty_revoke_unconfirmed", {
          status: result.status,
        });
      }
      return result.ok;
    };
    const firstPartyCookieCoordinator =
      createFirstPartyCookieRevocationCoordinator({
        clearIds: clearBrowserIds,
        keyOf: queuedFirstPartyCookieTargetKey,
        revoke: revokeFirstPartyTarget,
        sharedPending: {
          get: (target) =>
            storage.local.get(firstPartyCookieRevocationStorageKey(target)) ===
            "1",
          set: (target) =>
            storage.local.set(
              firstPartyCookieRevocationStorageKey(target),
              "1",
            ),
          clear: (target) =>
            storage.local.remove(firstPartyCookieRevocationStorageKey(target)),
        },
      });
    const revokeServerFirstPartyCookie = (options?: {
      clearBrowserIds?: boolean;
      apiBase?: string;
      siteKey?: string;
      cookieOnly?: boolean;
    }) => {
      const target = {
        apiBase: options?.apiBase ?? config.apiBase,
        siteKey: options?.siteKey ?? config.siteKey,
      };
      return firstPartyCookieCoordinator
        .revoke(target, {
          clearBrowserIds: options?.clearBrowserIds,
          ...(options?.cookieOnly
            ? { operation: () => expireFirstPartyTarget(target) }
            : {}),
        })
        .then((confirmed) => {
          // Run after the coordinator clears its pending marker, so analytics
          // plugins skipped during the ACK gap can now start when permitted.
          notifyConsentChanged();
          return confirmed;
        });
    };
    const scheduleQueuedFirstPartyCookieRevoke = (
      target: { apiBase: string; siteKey: string },
      clearBrowserIds = false,
      cookieOnly = false,
    ) =>
      firstPartyCookieCoordinator.scheduleRevoke(target, {
        clearBrowserIds,
        ...(cookieOnly
          ? { operation: () => expireFirstPartyTarget(target) }
          : {}),
      });

    const resolveCategoryMode = (category: DataCategory) => {
      const region = config.privacy.region;
      const regionOverride = region
        ? config.privacy.regionDataCategories?.[region]?.[category]
        : undefined;
      const globalMode = config.privacy.dataCategories?.[category];
      const mode = regionOverride ?? globalMode ?? "consented";
      if (mode === "off" || mode === "always" || mode === "consented") {
        return mode;
      }
      return "consented";
    };

    const canTrackCategory = (category: DataCategory) => {
      return canTrackCategoryWithSnapshot(consentSnapshot, category);
    };

    const canTrackCategoryWithSnapshot = (
      snapshot: ConsentSnapshot,
      category: DataCategory,
    ) => {
      const mode = resolveCategoryMode(category);
      if (mode === "off") return false;
      if (mode === "always") return canTrackWithSnapshot(snapshot, "essential");
      if (isActiveTestEnrollment(snapshot)) {
        if (
          category === "third_party_destinations" ||
          category === "audience_decisioning"
        )
          return false;
        if (
          [
            "identity_linkage",
            "identity_resolution",
            "geo_enrichment",
          ].includes(category)
        )
          return canTrackWithSnapshot(snapshot, "analytics");
      }
      const kind = DATA_CATEGORY_KINDS[category] ?? "analytics";
      return canTrackWithSnapshot(snapshot, kind);
    };

    const identityConsentAllowsSnapshot = (snapshot: ConsentSnapshot) =>
      (isActiveTestEnrollment(snapshot) ||
        canTrackWithSnapshot(snapshot, "marketing")) &&
      canTrackCategoryWithSnapshot(snapshot, "identity_linkage") &&
      snapshot.state === "granted" &&
      !snapshot.gpc &&
      !snapshot.dnt;
    const identityConsentAllows = () =>
      identityConsentAllowsSnapshot(consentSnapshot);
    const firstPartyCookiePluginEnabled = () =>
      isFirstPartyCookiePluginEnabled(
        config.plugins.disabled,
        flags.disabledPlugins,
      );
    const resolveFirstPartyPolicyForSnapshot = (snapshot: ConsentSnapshot) =>
      resolveFirstPartyIdentityPolicy({
        identityConsentAllowed: identityConsentAllowsSnapshot(snapshot),
        firstPartyCookiesEnabled:
          config.features?.firstPartyCookies !== false &&
          firstPartyCookiePluginEnabled(),
        cookieTransportVerified: isFirstPartyProbeConfirmed(),
        killSwitch: flags.killSwitch === true,
      });
    const resolveFirstPartyPolicy = () =>
      resolveFirstPartyPolicyForSnapshot(consentSnapshot);
    const firstPartyCookieIssueAllowed = () => {
      const target = { apiBase: config.apiBase, siteKey: config.siteKey };
      return (
        resolveFirstPartyPolicy().issueServerCookie &&
        !firstPartyCookieCoordinator.isPending(target)
      );
    };
    const identifierCreationAllowed = () =>
      resolveFirstPartyPolicy().createBrowserIds;
    const getPolicyBoundIds = () =>
      identifierCreationAllowed() ? getIds() : undefined;
    const identityCaptureReadiness = ():
      | { ready: true }
      | {
          ready: false;
          reason: "consent_denied" | "transport_failure" | "workspace_policy";
        } => {
      if (
        !config.identityCapture.enabled ||
        flags.killSwitch ||
        !config.siteKey ||
        resolveCategoryMode("identity_linkage") === "off"
      ) {
        return { ready: false, reason: "workspace_policy" };
      }
      if (
        consentSnapshot.state !== "granted" ||
        consentSnapshot.gpc ||
        consentSnapshot.dnt ||
        (consentSnapshot.cmp?.purposes?.marketing === false &&
          !isActiveTestEnrollment(consentSnapshot))
      ) {
        return { ready: false, reason: "consent_denied" };
      }
      if (!isFirstPartyProbeConfirmed()) {
        return { ready: false, reason: "transport_failure" };
      }
      if (!identityConsentAllows()) {
        return { ready: false, reason: "workspace_policy" };
      }
      return { ready: true };
    };
    const canTransmitIdentity = () => identityCaptureReadiness().ready;
    let synchronizeSharedConsent: (
      allowBroadening?: boolean,
    ) => string | null = () => readSharedRevocationTokenFromStorage();
    let handleAnalyticsConsentRevoked = () => {};
    let handleAnalyticsConsentGranted = () => {};
    let handleMarketingConsentRevoked = () => {};
    let handleMarketingConsentGranted = () => {};

    const buildCategoryPolicySnapshot = (consent = consentSnapshot) => {
      const policy: Record<string, boolean> = {};
      for (const category of DATA_CATEGORIES) {
        policy[category] = canTrackCategoryWithSnapshot(consent, category);
      }
      return policy;
    };

    const buildAuthorizedCategoryPolicySnapshot = (
      snapshot: ConsentSnapshot,
    ) => {
      const policy: Record<string, boolean> = {};
      for (const category of DATA_CATEGORIES) {
        const mode = resolveCategoryMode(category);
        const kind = DATA_CATEGORY_KINDS[category] ?? "analytics";
        policy[category] =
          mode !== "off" &&
          (mode === "always" ||
            kind === "essential" ||
            isConsentAuthorizedForSnapshot(snapshot, kind) ||
            (isActiveTestEnrollment(snapshot) &&
              [
                "identity_linkage",
                "identity_resolution",
                "geo_enrichment",
              ].includes(category)));
      }
      return policy;
    };

    const buildFirstPartyPolicySnapshot = (snapshot = consentSnapshot) => {
      const dataCategories = buildAuthorizedCategoryPolicySnapshot(snapshot);
      const identityAllowed =
        isActiveTestEnrollment(snapshot) ||
        isConsentAuthorizedForSnapshot(snapshot, "marketing");
      return {
        region: config.privacy.region,
        consentMode: resolveConsentMode(),
        // Revoke/regrant bodies describe the host's bounded purpose decision,
        // not the receipt fence currently enforcing it. Otherwise a 428 would
        // make the desired broadening all-false and impossible to resolve.
        firstPartyCookies:
          identityAllowed &&
          dataCategories.identity_linkage === true &&
          config.features?.firstPartyCookies !== false &&
          firstPartyCookiePluginEnabled(),
        dataCategories,
      };
    };

    const resolveFirstPartyPurposeSnapshot = (snapshot: ConsentSnapshot) => {
      const policy = buildFirstPartyPolicySnapshot(snapshot);
      if (isActiveTestEnrollment(snapshot))
        return {
          analytics: true,
          marketing: false,
          identity_resolution: true,
          enrichment: true,
        };
      return resolveFirstPartyConsentPurposeDecisions({
        analyticsAllowed: isConsentAuthorizedForSnapshot(snapshot, "analytics"),
        marketingAllowed: isConsentAuthorizedForSnapshot(snapshot, "marketing"),
        firstPartyCookies: policy.firstPartyCookies,
        identityLinkage: policy.dataCategories.identity_linkage === true,
      });
    };

    const buildCurrentFirstPartyCookieIssueRequest = () =>
      buildFirstPartyCookieIssueRequest({
        siteKey: config.siteKey,
        consent: consentSnapshot,
        policy: {
          ...buildFirstPartyPolicySnapshot(consentSnapshot),
          firstPartyCookies: firstPartyCookieIssueAllowed(),
        },
      });

    const attemptFirstPartyPurposeRegrant = async (options: {
      previous: ConsentSnapshot;
      next: ConsentSnapshot;
      transition: FirstPartyConsentTransition;
      generation: number;
    }) => {
      const target = { apiBase: config.apiBase, siteKey: config.siteKey };
      if (readFirstPartyTransitionRevisions(target).length === 0) {
        ensureFirstPartyTransitionRevision(target);
      }
      const transitionTokens = readFirstPartyTransitionRevisions(target).map(
        (revision) => revision.transitionToken,
      );
      if (
        transitionTokens.length === 0 ||
        transitionTokens.length > MAX_FIRST_PARTY_CONSENT_REVISION_HISTORY
      ) {
        return false;
      }
      const policy = buildFirstPartyPolicySnapshot(options.next);
      const nextPurposes = resolveFirstPartyPurposeSnapshot(options.next);
      const buildCurrentRequest = () => {
        if (
          options.generation !== consentTransitionGeneration ||
          !sharedConsentSnapshotsEqual(consentSnapshot, options.previous)
        ) {
          return null;
        }
        return buildFirstPartyCookieRegrantRequest({
          siteKey: target.siteKey,
          transitionToken: transitionTokens[0]!,
          transitionTokens,
          transition: options.transition,
          consent: options.next,
          policy,
        });
      };
      const url = `${target.apiBase.replace(/\/+$/, "")}/pxl/firstparty`;
      const confirmed = await firstPartyCookieCoordinator.issue(target, () =>
        regrantFirstPartyConsent({
          url,
          buildCurrentRequest,
          post: (requestUrl, payload, requestOptions) =>
            postWithRetry(requestUrl, payload, {
              beforeAttempt: requestOptions.beforeAttempt,
            }),
        }),
      );
      if (
        !confirmed ||
        options.generation !== consentTransitionGeneration ||
        !sharedConsentSnapshotsEqual(consentSnapshot, options.previous)
      ) {
        return false;
      }
      advanceFirstPartyConsentFenceGeneration(target);
      verifiedFirstPartyTransportTargets.add(firstPartyTargetKey(target));
      // A 2xx regrant is committed server-side before response. Every exact D
      // capability present in this browser has now been purpose-resolved, so
      // client retry history can be compacted even for a partial grant.
      clearFirstPartyTransitionToken(target);
      if (hasFullFirstPartyPurposeGrant(nextPurposes)) {
        unresolvedFirstPartyConsentPurposes.delete(firstPartyTargetKey(target));
      } else {
        markFirstPartyUnresolvedPurposes(target, nextPurposes);
      }
      return true;
    };

    const buildQueuedDeliveryContext = (
      type: string,
      deliveryClass: EventDeliveryClass,
      revocationToken: string | null,
      revocationAnonId?: string,
    ): QueuedDeliveryContext => {
      const essential = deliveryClass === "essential";
      if (essential && type === "consent_banner_interaction") {
        // Answer counting is independent of tracking permission. Never attach
        // test capabilities, browser identity, page context or revocation keys.
        return {
          apiBase: config.apiBase,
          siteKey: config.siteKey,
          deliveryClass: "essential",
          revocationToken: null,
          sdk: { ...config.sdk },
          consent: {
            state: consentSnapshot.state,
            gpc: consentSnapshot.gpc,
            dnt: consentSnapshot.dnt,
          },
          policy: {
            firstPartyCookies: false,
            dataCategories: { consent_analytics: true },
          },
        };
      }
      const fallbackPolicy = resolveAnonymousAnalyticsFallbackPolicy(
        deliveryClass,
        identifierCreationAllowed(),
      );
      const explicitConsentRevocation =
        type === "consent_update" &&
        requiresConsentRevocationBinding(consentSnapshot);
      const transitionTarget = {
        apiBase: config.apiBase,
        siteKey: config.siteKey,
      };
      const transitionRevision = explicitConsentRevocation
        ? resolveFirstPartyConsentRevisionForDelivery(
            readFirstPartyTransitionRevision(transitionTarget),
            blockedFirstPartyTransitionTargets.has(
              firstPartyTargetKey(transitionTarget),
            ),
          )
        : null;
      const revocation =
        explicitConsentRevocation && (revocationAnonId || transitionRevision)
          ? {
              ...(revocationAnonId ? { priorAnonId: revocationAnonId } : {}),
              ...(transitionRevision
                ? {
                    transitionToken: transitionRevision.transitionToken,
                    occurredAt: transitionRevision.occurredAt,
                  }
                : {}),
            }
          : undefined;
      const shouldAttachVisitor =
        !revocation && (!essential || type === "consent_update");
      // Explicit denial uses only the captured prior anonymous identifier. It
      // must not carry a session or create a new browser identifier.
      const ids =
        fallbackPolicy.attachVisitor && shouldAttachVisitor
          ? essential && !identifierCreationAllowed()
            ? idManager.getExistingIds()
            : getPolicyBoundIds()
          : undefined;
      const rawContext = !fallbackPolicy.attachContext
        ? undefined
        : buildContext(config, redactor.sanitizeUrl);
      const filteredContext = {
        ...(rawContext && canTrackCategory("context_page")
          ? { page: rawContext.page }
          : {}),
        ...(rawContext && canTrackCategory("context_device")
          ? { device: rawContext.device }
          : {}),
        ...(rawContext && canTrackCategory("marketing_params")
          ? { marketing: rawContext.marketing }
          : {}),
        ...(rawContext && canTrackCategory("network_hints")
          ? { network: rawContext.network }
          : {}),
      };
      const context =
        !essential &&
        fallbackPolicy.attachContext &&
        Object.keys(filteredContext).length > 0
          ? filteredContext
          : undefined;
      const consent = normalizeSharedConsentSnapshot(consentSnapshot) ?? {
        state: consentSnapshot.state,
        gpc: consentSnapshot.gpc,
        dnt: consentSnapshot.dnt,
      };
      return {
        apiBase: config.apiBase,
        deliveryClass,
        revocationToken,
        siteKey: config.siteKey,
        sdk: { ...config.sdk },
        consent,
        policy: {
          region: config.privacy.region,
          consentMode: resolveConsentMode(),
          firstPartyCookies: firstPartyCookieIssueAllowed(),
          dataCategories: buildCategoryPolicySnapshot(),
        },
        ...(ids
          ? { visitor: { anonId: ids.anonId, sessionId: ids.sessionId } }
          : {}),
        ...(revocation ? { revocation } : {}),
        ...(context ? { context } : {}),
      };
    };

    const resolveQueuedDeliveryClass = (
      event: QueuedEvent,
    ): EventDeliveryClass | null => {
      const deliveryClass = event.delivery?.deliveryClass;
      if (
        deliveryClass === "essential" ||
        deliveryClass === "analytics" ||
        deliveryClass === "marketing"
      ) {
        return deliveryClass;
      }
      // Pre-envelope event names were caller-controlled. Without an immutable
      // source/privacy class, recovery must fail closed.
      return null;
    };

    let warnedMissingSiteKey = false;
    const trackEvent = (
      type: string,
      props?: Record<string, unknown>,
      requiredClass?: EventDeliveryClass,
      options?: {
        revocationAnonId?: string;
        consentEvidence?: ConsentEvidenceOutboxMarker;
        consentEvidenceSnapshot?: ConsentSnapshot;
      },
    ): { durable: boolean } => {
      const deliveryClass = resolveEventDeliveryClass(type, requiredClass);
      const stagedSharedRevocationToken = synchronizeSharedConsent();
      const stagedRevocationGeneration =
        deliveryClass === "marketing"
          ? marketingRevocationGeneration
          : analyticsRevocationGeneration;
      const explicitConsentRestriction =
        type === "consent_update" &&
        requiresConsentRevocationBinding(consentSnapshot);
      if (
        flags.killSwitch &&
        !(
          deliveryClass === "essential" &&
          type === "consent_update" &&
          explicitConsentRestriction
        )
      ) {
        return { durable: false };
      }
      if (!config.siteKey) {
        if (!warnedMissingSiteKey) {
          logger.warn("missing_site_key");
          warnedMissingSiteKey = true;
        }
        return { durable: false };
      }
      const essential = deliveryClass === "essential";
      if (!canTrack(deliveryClass)) return { durable: false };
      const fallbackPolicy = resolveAnonymousAnalyticsFallbackPolicy(
        deliveryClass,
        identifierCreationAllowed(),
      );
      if (fallbackPolicy.active && !isAnonymousAnalyticsFallbackEvent(type)) {
        return { durable: false };
      }
      if (!essential && Math.random() > config.plugins.sampling.rate) {
        return { durable: false };
      }
      if (!pageLifecycleEventGate.shouldTrack(type, config.siteKey)) {
        return { durable: false };
      }

      if (!essential && fallbackPolicy.attachVisitor) {
        // A heartbeat is evidence within an existing visit, never evidence
        // that another visit began after the browser was idle or suspended.
        if (
          ["engagement", "session_quality", "return_visit"].includes(type) &&
          !idManager.getActiveIds()
        ) {
          return { durable: false };
        }
        // Ensure stable visitor/session IDs for queued analytics events.
        getPolicyBoundIds();
      }

      // Redact and sample before enqueueing to avoid persisting sensitive data.
      const attribution =
        !essential &&
        fallbackPolicy.attachProperties &&
        type !== "attribution_update" &&
        canTrackCategory("attribution")
          ? readAttributionSnapshot(storage.local)
          : undefined;
      const propsWithAttribution = attribution
        ? { ...(props ?? {}), attribution }
        : props;
      const sanitizedProps =
        fallbackPolicy.attachProperties &&
        propsWithAttribution &&
        typeof propsWithAttribution === "object"
          ? type === "replay_chunk"
            ? (propsWithAttribution as Record<string, unknown>)
            : redactor.sanitizeObject(propsWithAttribution)
          : undefined;
      const event: TrackEvent = {
        eventId: ulid(),
        type,
        occurredAt: now(),
        props: sanitizedProps,
      };

      const currentSharedRevocationToken = synchronizeSharedConsent();
      const consentEvidenceRevisionCurrent =
        !options?.consentEvidence ||
        (options.consentEvidence.revisionToken ===
          stagedSharedRevocationToken &&
          options.consentEvidence.revisionToken ===
            currentSharedRevocationToken);
      const consentEvidenceSnapshotCurrent =
        !options?.consentEvidenceSnapshot ||
        isConsentEvidenceSnapshotCurrent(
          options.consentEvidenceSnapshot,
          consentSnapshot,
        );
      if (
        !consentEvidenceRevisionCurrent ||
        !consentEvidenceSnapshotCurrent ||
        !isConsentBoundDeliveryCurrent({
          isEssential: essential,
          currentlyAllowed: canTrack(deliveryClass),
          stagedRevocationGeneration,
          currentRevocationGeneration:
            deliveryClass === "marketing"
              ? marketingRevocationGeneration
              : analyticsRevocationGeneration,
          stagedSharedRevocationToken,
          currentSharedRevocationToken,
        })
      ) {
        return { durable: false };
      }

      const delivery = buildQueuedDeliveryContext(
        type,
        deliveryClass,
        stagedSharedRevocationToken,
        explicitConsentRestriction ? options?.revocationAnonId : undefined,
      );
      const enqueued = queue.enqueue({
        eventId: event.eventId,
        type: event.type,
        occurredAt: event.occurredAt,
        props: event.props,
        enqueuedAt: now(),
        attempts: 0,
        ...(options?.consentEvidence
          ? { consentEvidence: options.consentEvidence }
          : {}),
        delivery,
      });

      if (enqueued.durable && type === "page_view" && delivery.visitor) {
        lastPageView = {
          siteKey: config.siteKey,
          revocationToken: stagedSharedRevocationToken,
          observation: {
            eventId: event.eventId,
            occurredAt: event.occurredAt,
            ...delivery.visitor,
          },
        };
      }
      bus.emit("track", event);
      if (essential && type === "consent_banner_interaction") {
        void flush({ reason: "consent_response" });
      }
      return enqueued;
    };

    const captureIdentity = async (
      capture: FirstPartyIdentityCapture,
      deliveryGuard?: () => boolean,
    ): Promise<boolean> => {
      const stagedSharedRevocationToken = synchronizeSharedConsent();
      if (!canTransmitIdentity() || (deliveryGuard && !deliveryGuard()))
        return false;
      const stagedIdentityRevocationGeneration = identityRevocationGeneration;

      const normalize = (value: unknown, max: number) =>
        typeof value === "string" && value.trim()
          ? value.trim().slice(0, max)
          : undefined;
      const normalizedEmail = normalize(capture.email, 320)?.toLowerCase();
      const email =
        normalizedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
          ? normalizedEmail
          : undefined;
      const phone = normalize(capture.phone, 40);
      const externalId = normalize(capture.externalId, 256);
      const profile = capture.profile
        ? {
            fullName: normalize(capture.profile.fullName, 200),
            company: normalize(capture.profile.company, 200),
            jobTitle: normalize(capture.profile.jobTitle, 200),
            website: normalize(capture.profile.website, 2048),
            first: normalize(capture.profile.first, 100),
            middle: normalize(capture.profile.middle, 100),
            last: normalize(capture.profile.last, 100),
            street: normalize(capture.profile.street, 200),
            city: normalize(capture.profile.city, 120),
            state: normalize(capture.profile.state, 120),
            postalCode: normalize(capture.profile.postalCode, 32),
            country: normalize(capture.profile.country, 64),
          }
        : undefined;
      const hasProfile = Boolean(
        profile && Object.values(profile).some(Boolean),
      );
      const enquiryProof = normalize(capture.enquiryProof, 2048);
      const enquiryAttributionProof = normalize(
        capture.enquiryAttributionProof,
        2048,
      );
      if (
        enquiryAttributionProof &&
        (capture.enquiryAttributionProof!.length > 2048 ||
          enquiryProof ||
          email ||
          phone ||
          externalId ||
          hasProfile ||
          capture.source !== "browser_enquiry")
      )
        return false;
      const companyVisit =
        capture.source === "visit_company" &&
        config.identityCapture.companyLookup === true;
      if (
        !email &&
        !phone &&
        !externalId &&
        !hasProfile &&
        !enquiryProof &&
        !enquiryAttributionProof &&
        !companyVisit
      )
        return false;
      const source = /^[a-z0-9_.:-]{1,64}$/i.test(capture.source)
        ? capture.source
        : "identify_api";
      const configuredPurposes = new Set(config.identityCapture.purposes);
      const purposes = (capture.purposes ?? config.identityCapture.purposes)
        .filter((purpose) => configuredPurposes.has(purpose))
        .slice(0, 3);
      if (purposes.length === 0) return false;
      const ids = enquiryAttributionProof
        ? idManager.getExistingIds()
        : getIds();
      if (!ids) return false;
      const payload = {
        siteKey: config.siteKey,
        occurredAt: now(),
        source,
        purposes,
        visitor: { anonId: ids.anonId, sessionId: ids.sessionId },
        ...(enquiryAttributionProof
          ? {}
          : { identity: { email, phone, externalId } }),
        ...(enquiryProof ? { enquiryProof } : {}),
        ...(enquiryAttributionProof ? { enquiryAttributionProof } : {}),
        ...(hasProfile ? { profile } : {}),
        consent: {
          state: consentSnapshot.state,
          gpc: consentSnapshot.gpc,
          dnt: consentSnapshot.dnt,
          ...(consentSnapshot.cmp
            ? {
                cmp: {
                  source: consentSnapshot.cmp.source,
                  tcString: consentSnapshot.cmp.tcString,
                  uspString: consentSnapshot.cmp.uspString,
                  gppString: consentSnapshot.cmp.gppString,
                  gppSectionIds: consentSnapshot.cmp.gppSectionIds,
                  purposes: consentSnapshot.cmp.purposes,
                  ...(consentSnapshot.cmp.source === TEST_ENROLLMENT_SOURCE
                    ? {
                        testEnrollmentToken:
                          consentSnapshot.cmp.testEnrollmentToken,
                        testEnrollmentBrowserId:
                          consentSnapshot.cmp.testEnrollmentBrowserId,
                        testEnrollmentExpiresAt:
                          consentSnapshot.cmp.testEnrollmentExpiresAt,
                      }
                    : {}),
                },
              }
            : {}),
        },
      };
      const endpoint = config.identityCapture.endpointPath || "/pxl/identity";
      const url = `${config.apiBase.replace(/\/+$/, "")}${endpoint}`;
      const result = await postWithRetryResult(url, payload, {
        beforeAttempt: () => {
          const currentSharedRevocationToken = synchronizeSharedConsent();
          if (deliveryGuard && !deliveryGuard()) return false;
          return isConsentBoundDeliveryCurrent({
            isEssential: false,
            currentlyAllowed: canTransmitIdentity(),
            stagedRevocationGeneration: stagedIdentityRevocationGeneration,
            currentRevocationGeneration: identityRevocationGeneration,
            stagedSharedRevocationToken,
            currentSharedRevocationToken,
          });
        },
      });
      observeTestCaptureResponse(
        result.status,
        payload.consent.cmp?.testEnrollmentToken,
      );
      return result.ok;
    };

    const captureCompanyVisit = createCompanyVisitCapture({
      enabled: () => config.identityCapture.companyLookup === true,
      allowed: () => canTransmitIdentity(),
      capture: captureIdentity,
    });

    const recordConsentEvidence = (
      next: ConsentSnapshot,
      signalSource?: string,
      priorAnonId?: string,
    ) => {
      const revisionToken = synchronizeSharedConsent();
      const resolvedSnapshot = resolveCurrentConsentEvidenceSnapshot(
        next,
        consentSnapshot,
      );
      if (!resolvedSnapshot || !consentEvidenceKey) {
        currentConsentEvidenceMarker = undefined;
        return;
      }
      const evidence = buildConsentEvidence(
        resolvedSnapshot.snapshot,
        resolvedSnapshot.requestedIsCurrent ? signalSource : undefined,
      );
      if (!evidence) {
        currentConsentEvidenceMarker = undefined;
        return;
      }
      currentConsentEvidenceMarker = {
        fingerprint: evidence.fingerprint,
        revisionToken,
      };
      commitConsentEvidenceToDurableOutbox({
        evidence,
        fingerprintKey: consentEvidenceKey,
        revisionToken,
        storage: storage.local,
        hasDurableEvidence: (marker) =>
          queue.someDurable(
            (event) =>
              event.type === "consent_update" &&
              event.consentEvidence?.fingerprint === marker.fingerprint &&
              event.consentEvidence.revisionToken === marker.revisionToken,
          ),
        enqueue: (payload, marker) =>
          trackEvent("consent_update", payload, "essential", {
            consentEvidence: marker,
            consentEvidenceSnapshot: resolvedSnapshot.snapshot,
            revocationAnonId: requiresConsentRevocationBinding(
              resolvedSnapshot.snapshot,
            )
              ? priorAnonId
              : undefined,
          }),
      });
    };

    const applyConsentSnapshot = (next: ConsentSnapshot) => {
      const analyticsWasAllowed = canTrack("analytics");
      const marketingWasAllowed = canTrack("marketing");
      const identityWasAllowed = identityConsentAllows();
      // Any in-flight success for the prior decision must not acknowledge the
      // evidence marker for a newer snapshot. recordConsentEvidence installs
      // the new marker after the transition is durably fenced.
      currentConsentEvidenceMarker = undefined;
      consentSnapshot = next;
      const analyticsIsAllowed = canTrack("analytics");
      const marketingIsAllowed = canTrack("marketing");
      const identityIsAllowed = identityConsentAllows();
      const lifecycle = resolveConsentLifecycleActions({
        analyticsWasAllowed,
        analyticsIsAllowed,
        marketingWasAllowed,
        marketingIsAllowed,
      });
      if (analyticsWasAllowed && !analyticsIsAllowed) {
        analyticsRevocationGeneration += 1;
      }
      if (marketingWasAllowed && !marketingIsAllowed) {
        marketingRevocationGeneration += 1;
      }
      if (identityWasAllowed && !identityIsAllowed) {
        identityRevocationGeneration += 1;
      }
      const revokedNonEssential =
        (analyticsWasAllowed && !analyticsIsAllowed) ||
        (marketingWasAllowed && !marketingIsAllowed) ||
        (identityWasAllowed && !identityIsAllowed);
      if (
        revokedNonEssential ||
        !isConsentAuthorizedForSnapshot(next, "analytics")
      ) {
        // Once consent is revoked, even an older essential grant envelope is
        // unsafe to deliver. A reload's pending cookie probe is a delivery
        // hold, not withdrawal of the stored permission: retain its queued
        // form/visit evidence until the unchanged transport gates settle.
        retainOnlyEssentialQueuedEvents(queue, isPrivacyEssentialQueuedEvent);
      }
      if (lifecycle.revokeMarketing) {
        handleMarketingConsentRevoked();
      } else if (lifecycle.grantMarketing) {
        handleMarketingConsentGranted();
      }
      if (lifecycle.revokeAnalytics) {
        handleAnalyticsConsentRevoked();
      } else if (lifecycle.grantAnalytics) {
        handleAnalyticsConsentGranted();
      }
      return {
        revokedNonEssential,
        identityRevoked: identityWasAllowed && !identityIsAllowed,
      };
    };

    const notifyConsentChanged = () => {
      bus.emit("consent", consentSnapshot);
      window.dispatchEvent(
        new CustomEvent("prospix:consent-changed", {
          detail: {
            state: consentSnapshot.state,
            gpc: consentSnapshot.gpc,
            dnt: consentSnapshot.dnt,
          },
        }),
      );
    };

    const setConsent = async (
      next: ConsentSnapshot,
      emitEvent = true,
      signalSource?: string,
      persistSharedState = true,
      transition?: Partial<FirstPartyConsentTransition>,
    ) => {
      const transitionGeneration = ++consentTransitionGeneration;
      const previousSnapshot = consentSnapshot;
      // Capture without creating before any denial path clears browser IDs.
      // This value is used only in the revocation-only consent envelope.
      const priorAnonId = idManager.getExistingAnonId();
      const canPersistSharedState = Boolean(
        persistSharedState && consentKey && consentRevocationKey,
      );
      const priorSharedState = canPersistSharedState
        ? readSharedConsentStorageState()
        : null;
      let resolvedWrite = resolveConsentSnapshotForSharedWrite(next, {
        fenceMismatch: priorSharedState?.fenceMismatch === true,
        signalSource,
      });
      const resolveNonEssentialAllowance = (snapshot: ConsentSnapshot) => ({
        analyticsAllowed: isConsentAuthorizedForSnapshot(snapshot, "analytics"),
        marketingAllowed: isConsentAuthorizedForSnapshot(snapshot, "marketing"),
      });
      if (
        shouldHoldConsentPersistenceLatch({
          persistenceFailed: sharedConsentPersistenceFailed,
          signalSource,
          ...resolveNonEssentialAllowance(resolvedWrite.snapshot),
        })
      ) {
        resolvedWrite = {
          snapshot: buildStorageFailureConsentSnapshot(resolvedWrite.snapshot),
          failureSource: "storage_persistence_failure",
        };
      }
      let effectiveNext = resolvedWrite.snapshot;
      let serverReceiptRestriction = signalSource === "firstparty_receipt";
      const freshTransition = isFreshFirstPartyConsentTransition(transition);
      const previousAllowance = resolveNonEssentialAllowance(previousSnapshot);
      let nextAllowance = resolveNonEssentialAllowance(effectiveNext);
      const purposeBroadeningRequested = hasFirstPartyDeliveryBroadening(
        previousAllowance,
        nextAllowance,
      );
      const resolveServerFenceBroadeningRequested = () =>
        (nextAllowance.analyticsAllowed &&
          isFirstPartyPurposeFenced("analytics")) ||
        (nextAllowance.marketingAllowed &&
          (isFirstPartyPurposeFenced("marketing") ||
            isFirstPartyPurposeFenced("identity_resolution") ||
            isFirstPartyPurposeFenced("enrichment")));
      let serverFenceBroadeningRequested =
        resolveServerFenceBroadeningRequested();
      const candidateBroadening =
        purposeBroadeningRequested || serverFenceBroadeningRequested;
      if (candidateBroadening) {
        const hadKnownFence =
          hasLocalFirstPartyConsentFence() ||
          isFirstPartyPurposeFenced("analytics") ||
          isFirstPartyPurposeFenced("marketing") ||
          isFirstPartyPurposeFenced("identity_resolution") ||
          isFirstPartyPurposeFenced("enrichment");
        const previousExplicitAnalyticsDenial =
          previousSnapshot.state === "denied" ||
          previousSnapshot.gpc ||
          previousSnapshot.dnt ||
          previousSnapshot.cmp?.purposes?.analytics === false;
        let transportVerified = await ensureFirstPartyCookieTransportProbe(
          currentFirstPartyTarget(),
          true,
        );
        if (!transportVerified && freshTransition) {
          // A preceding denial ACK can invalidate a probe started in the same
          // loader turn. Recheck once for the original fresh action, retaining
          // its timestamp; a stored preference cannot take this retry path.
          transportVerified = await ensureFirstPartyCookieTransportProbe(
            currentFirstPartyTarget(),
            true,
          );
        }
        if (
          !transportVerified &&
          (hadKnownFence || previousExplicitAnalyticsDenial)
        ) {
          return consentSnapshot;
        }
        serverFenceBroadeningRequested =
          resolveServerFenceBroadeningRequested();
      }
      const heldByFirstPartyFence = shouldHoldFirstPartyConsentFence({
        analyticsFenced: isFirstPartyPurposeFenced("analytics"),
        marketingFenced:
          isFirstPartyPurposeFenced("marketing") ||
          isFirstPartyPurposeFenced("identity_resolution") ||
          isFirstPartyPurposeFenced("enrichment"),
        freshTransition,
        ...nextAllowance,
      });
      if (heldByFirstPartyFence) {
        const purposeFencedNext = applyFirstPartyPurposeFence(effectiveNext);
        const purposeFencedAllowance =
          resolveNonEssentialAllowance(purposeFencedNext);
        if (
          shouldHoldFirstPartyConsentFence({
            analyticsFenced: isFirstPartyPurposeFenced("analytics"),
            marketingFenced:
              isFirstPartyPurposeFenced("marketing") ||
              isFirstPartyPurposeFenced("identity_resolution") ||
              isFirstPartyPurposeFenced("enrichment"),
            freshTransition,
            ...purposeFencedAllowance,
          })
        ) {
          // Reloads, delayed CMP callbacks, and cross-tab grants cannot broaden a
          // server-observed denial receipt. Only a fresh authoritative user
          // action may enter the regrant ceremony.
          return consentSnapshot;
        }
        // A purpose-aware 428 may still authorize coarse analytics while its
        // marketing/identity purposes remain fenced. Apply only that bounded
        // partial snapshot; do not discard it with the blocked high-risk grant.
        effectiveNext = purposeFencedNext;
        nextAllowance = purposeFencedAllowance;
        serverReceiptRestriction = true;
      }
      const previousPurposes =
        resolveFirstPartyPurposeSnapshot(previousSnapshot);
      const nextPurposes = resolveFirstPartyPurposeSnapshot(effectiveNext);
      if (
        freshTransition &&
        (hasFirstPartyPurposeBroadening(previousPurposes, nextPurposes) ||
          serverFenceBroadeningRequested)
      ) {
        const regrantConfirmed = await attemptFirstPartyPurposeRegrant({
          previous: previousSnapshot,
          next: effectiveNext,
          transition,
          generation: transitionGeneration,
        });
        if (!regrantConfirmed) return consentSnapshot;
      }
      if (transitionGeneration !== consentTransitionGeneration) {
        return consentSnapshot;
      }
      const applied = applyConsentSnapshot(effectiveNext);
      const revokedNonEssential = applied.revokedNonEssential;
      let identityRevoked = applied.identityRevoked;
      const purposeRestricted = (
        Object.keys(nextPurposes) as Array<keyof typeof nextPurposes>
      ).some(
        (purpose) =>
          previousPurposes[purpose] === true && nextPurposes[purpose] !== true,
      );
      const explicitRestrictionChanged = Boolean(
        freshTransition &&
        !isActiveTestEnrollment(effectiveNext) &&
        !sharedConsentSnapshotsEqual(previousSnapshot, effectiveNext) &&
        Object.values(nextPurposes).some((allowed) => !allowed),
      );
      if (
        !serverReceiptRestriction &&
        (purposeRestricted || explicitRestrictionChanged)
      ) {
        ensureFirstPartyTransitionToken(
          { apiBase: config.apiBase, siteKey: config.siteKey },
          persistSharedState,
          freshTransition ? transition.occurredAt : now(),
        );
      }
      const consentRestrictsIdentity = !(
        isActiveTestEnrollment(effectiveNext) ||
        isConsentAuthorizedForSnapshot(effectiveNext, "marketing")
      );
      const identityCategoryDisabled =
        resolveCategoryMode("identity_linkage") === "off";
      const cookiePolicyDisabled =
        config.features?.firstPartyCookies === false ||
        !firstPartyCookiePluginEnabled() ||
        identityCategoryDisabled;
      if (
        shouldRevokeFirstPartyCookie({
          siteKey: config.siteKey,
          // Probe/bootstrap state is not a consent revocation. A transiently
          // unverified transport simply remains aggregate-only; it must not
          // mint a denial capability for an otherwise affirmative decision.
          identityAllowed:
            !serverReceiptRestriction &&
            !consentRestrictsIdentity &&
            !cookiePolicyDisabled,
          identityRevoked: false,
        })
      ) {
        if (consentRestrictsIdentity && !serverReceiptRestriction) {
          // Invalidate any already-settled probe callback in this same task
          // before the coordinator yields to its queued network operation. A
          // stale 204 must never clear the restriction established here.
          advanceFirstPartyConsentFenceGeneration(currentFirstPartyTarget());
          // Publish the shared per-target pending marker before the shared
          // consent denial becomes visible. A sibling tab must never reissue
          // or reuse the old HttpOnly identifier during this ACK gap.
          ensureFirstPartyTransitionToken({
            apiBase: config.apiBase,
            siteKey: config.siteKey,
          });
          void revokeServerFirstPartyCookie();
        } else {
          // A server receipt fence or a host cookie/category feature switch
          // expires identity material without creating another consent
          // capability. Receipt-enforced restrictions still clear local IDs;
          // a cookie-feature-only switch deliberately preserves them.
          void revokeServerFirstPartyCookie({
            clearBrowserIds:
              serverReceiptRestriction || identityCategoryDisabled,
            cookieOnly: true,
          });
        }
      }
      let evidenceSnapshot = effectiveNext;
      let evidenceSignalSource = resolvedWrite.failureSource ?? signalSource;
      if (
        canPersistSharedState &&
        consentKey !== undefined &&
        consentRevocationKey !== undefined
      ) {
        const sharedSnapshot =
          normalizeSharedConsentSnapshot(effectiveNext) ?? consentSnapshot;
        const nextRevocationToken =
          revokedNonEssential || priorSharedState!.fenceMismatch
            ? ulid()
            : priorSharedState!.revocationToken;
        const persisted = persistSharedConsentRecord(
          storage.local,
          consentKey,
          consentRevocationKey,
          {
            ...sharedSnapshot,
            revocationToken: nextRevocationToken,
            updatedAt: now(),
          },
        );
        if (!persisted.persisted) {
          // Latch before emitting evidence: evidence tracking reconciles shared
          // state, and must not resurrect the prior durable grant when a
          // no-op/partial storage adapter left that old record internally
          // consistent.
          sharedConsentPersistenceFailed = true;
          // If either half of the compatibility write failed, stop all
          // nonessential collection locally until both fences converge.
          const failClosedSnapshot =
            buildStorageFailureConsentSnapshot(effectiveNext);
          const failClosedApplied = applyConsentSnapshot(failClosedSnapshot);
          if (failClosedApplied.identityRevoked && !identityRevoked) {
            ensureFirstPartyTransitionToken(
              { apiBase: config.apiBase, siteKey: config.siteKey },
              true,
              now(),
            );
            void revokeServerFirstPartyCookie();
          }
          identityRevoked ||= failClosedApplied.identityRevoked;
          evidenceSnapshot = failClosedSnapshot;
          evidenceSignalSource = "storage_persistence_failure";
        } else {
          lastObservedSharedConsentRaw = storage.local.get(consentKey);
          lastObservedSharedRevocationRaw =
            storage.local.get(consentRevocationKey);
          // Only a locally verified atomic write may release the fail-closed
          // persistence latch. Cross-tab/internal grant reads cannot clear it;
          // a verified denial or genuinely explicit manual choice can.
          if (
            !shouldHoldConsentPersistenceLatch({
              persistenceFailed: sharedConsentPersistenceFailed,
              signalSource,
              ...resolveNonEssentialAllowance(sharedSnapshot),
            })
          ) {
            sharedConsentPersistenceFailed = false;
          }
        }
      }
      notifyConsentChanged();
      if (emitEvent) {
        // Denial evidence is essential and must survive the same storage
        // failure that forced the runtime to fail closed.
        recordConsentEvidence(
          evidenceSnapshot,
          evidenceSignalSource,
          priorAnonId,
        );
      }
      return consentSnapshot;
    };

    synchronizeSharedConsent = (allowBroadening = false) => {
      if (!consentKey || !consentRevocationKey) return null;
      const rawRecord = storage.local.get(consentKey);
      const legacyRevocationToken = storage.local.get(consentRevocationKey);
      const externallyChanged =
        rawRecord !== lastObservedSharedConsentRaw ||
        legacyRevocationToken !== lastObservedSharedRevocationRaw;
      lastObservedSharedConsentRaw = rawRecord;
      lastObservedSharedRevocationRaw = legacyRevocationToken;
      const synchronized = resolveSharedConsentSynchronization({
        persistenceFailed: sharedConsentPersistenceFailed,
        current: consentSnapshot,
        rawRecord,
        legacyRevocationToken,
      });
      if (
        currentConsentEvidenceMarker &&
        currentConsentEvidenceMarker.revisionToken !==
          synchronized.revocationToken
      ) {
        currentConsentEvidenceMarker = undefined;
      }
      const target = currentFirstPartyTarget();
      const targetKey = firstPartyTargetKey(target);
      const synchronizedAllowance = {
        analyticsAllowed: isConsentAuthorizedForSnapshot(
          synchronized.snapshot,
          "analytics",
        ),
        marketingAllowed: isConsentAuthorizedForSnapshot(
          synchronized.snapshot,
          "marketing",
        ),
      };
      const authorizesNonEssential =
        synchronizedAllowance.analyticsAllowed ||
        synchronizedAllowance.marketingAllowed;

      if (externallyChanged) {
        advanceFirstPartyConsentFenceGeneration(target);
        if (authorizesNonEssential) {
          // Treat every external/shared grant as untrusted until this cookie
          // partition obtains a fresh authoritative 204/428. This also covers
          // a cached-v1 tab creating a new hidden D receipt after our last 204.
          sharedGrantProbePendingTargets.add(targetKey);
          void ensureFirstPartyCookieTransportProbe(target, true).then(
            (transportVerified) => {
              if (!transportVerified) return;
              sharedGrantProbePendingTargets.delete(targetKey);
              synchronizeSharedConsent(true);
            },
          );
        } else {
          // Restrictions apply immediately and invalidate any older probe.
          sharedGrantProbePendingTargets.delete(targetKey);
        }
      }

      const holdForAuthoritativeProbe =
        authorizesNonEssential &&
        (!isFirstPartyTransportVerified(target) ||
          // A pending local restriction is a delivery hold, not a new choice
          // to deny every purpose. Keep the chosen snapshot so the outgoing
          // revoke records (for example) marketing=false, analytics=true.
          hasLocalFirstPartyConsentFence(target) ||
          sharedGrantProbePendingTargets.has(targetKey));
      const purposeFencedSnapshot = holdForAuthoritativeProbe
        ? consentSnapshot
        : applyFirstPartyPurposeFence(synchronized.snapshot, target);
      const currentAllowance = {
        analyticsAllowed: isConsentAuthorizedForSnapshot(
          consentSnapshot,
          "analytics",
        ),
        marketingAllowed: isConsentAuthorizedForSnapshot(
          consentSnapshot,
          "marketing",
        ),
      };
      const synchronizedSnapshot =
        !allowBroadening &&
        hasFirstPartyDeliveryBroadening(currentAllowance, {
          analyticsAllowed: isConsentAuthorizedForSnapshot(
            purposeFencedSnapshot,
            "analytics",
          ),
          marketingAllowed: isConsentAuthorizedForSnapshot(
            purposeFencedSnapshot,
            "marketing",
          ),
        })
          ? consentSnapshot
          : purposeFencedSnapshot;
      if (!sharedConsentSnapshotsEqual(synchronizedSnapshot, consentSnapshot)) {
        consentTransitionGeneration += 1;
        const applied = applyConsentSnapshot(synchronizedSnapshot);
        notifyConsentChanged();
        if (applied.identityRevoked) {
          void revokeServerFirstPartyCookie();
        }
      }
      return synchronized.revocationToken;
    };

    const handleSharedConsentChange = (event: StorageEvent) => {
      if (event.key === consentKey) {
        synchronizeSharedConsent();
        return;
      }
      if (event.key === consentRevocationKey) {
        synchronizeSharedConsent();
        return;
      }
      if (event.key === "prospix_optout" && event.newValue === "1") {
        void (async () => {
          await setConsent(
            {
              state: "denied",
              gpc: consentSnapshot.gpc,
              dnt: consentSnapshot.dnt,
            },
            false,
            "storage",
            false,
          );
          retainOnlyEssentialQueuedEvents(queue, isPrivacyEssentialQueuedEvent);
          clearBrowserIds();
          await unloadPlugins();
          void revokeServerFirstPartyCookie();
          await flush({ reason: "cross_tab_optout" });
        })();
      }
    };
    window.addEventListener("storage", handleSharedConsentChange);
    window.addEventListener(
      "unload",
      () => window.removeEventListener("storage", handleSharedConsentChange),
      { once: true },
    );

    const refreshConsent = async () => {
      const resolved = await resolveConsent(config, readConsentFromStorage());
      await setConsent(resolved);
    };

    type FlushOptions = { useBeacon?: boolean; reason?: string };
    let flushInFlight = false;
    let flushQueued: FlushOptions | undefined;

    // Build an envelope and deliver via sendBeacon/fetch with bounded retries.
    const flushInternal = async ({
      useBeacon = false,
      reason = "timer",
    }: FlushOptions = {}) => {
      synchronizeSharedConsent();
      if (flags.killSwitch || storage.local.get("prospix_optout") === "1") {
        // A durable denial may still be awaiting an HTTP acknowledgement.
        // Purge all other work, but keep retrying explicit denial evidence
        // instead of treating sendBeacon acceptance as delivery.
        retainOnlyEssentialQueuedEvents(
          queue,
          flags.killSwitch
            ? isExplicitDenialQueuedEvent
            : isPrivacyEssentialQueuedEvent,
        );
      }
      // Retry revocations in the background. Pending old-policy envelopes are
      // withheld below, while explicit denial and policy=false envelopes can
      // proceed without waiting on an unrelated or unreachable origin.
      firstPartyCookieCoordinator.retryPending();
      const batch = queue.peek();
      if (!batch.length) return;

      const pendingFirstPartyCookieRevocations = new Set(
        firstPartyCookieCoordinator.pendingTargetKeys(),
      );
      for (const event of batch) {
        const target = queuedFirstPartyCookieTarget(event);
        if (!target || !firstPartyCookieCoordinator.isPending(target)) {
          continue;
        }
        pendingFirstPartyCookieRevocations.add(
          queuedFirstPartyCookieTargetKey(target),
        );
        scheduleQueuedFirstPartyCookieRevoke(target, false);
      }
      const candidates = batch.filter(
        (event) =>
          !shouldWithholdForPendingCookieRevoke(
            event,
            pendingFirstPartyCookieRevocations,
          ),
      );
      const allowed = candidates.filter((event) => {
        const deliveryClass = resolveQueuedDeliveryClass(event);
        if (flags.killSwitch) return isExplicitDenialQueuedEvent(event);
        // Diagnostics describe the permission at observation time. Sending an
        // old blocked diagnostic after a fresh grant can recreate a server
        // denial capability through its immutable no-cookie envelope. Genuine
        // consent updates retain their durable delivery/acknowledgement path.
        if (
          deliveryClass === "essential" &&
          event.type !== "consent_update" &&
          !isAnonymousConsentResponseQueuedEvent(event) &&
          event.delivery &&
          !sharedConsentSnapshotsEqual(event.delivery.consent, consentSnapshot)
        )
          return false;
        return deliveryClass !== null && canTrack(deliveryClass);
      });
      const dropped = candidates.length - allowed.length;
      if (dropped) {
        const allowedIds = new Set(allowed.map((event) => event.eventId));
        queue.remove(
          candidates
            .filter((event) => !allowedIds.has(event.eventId))
            .map((event) => event.eventId),
        );
      }
      if (!allowed.length) return;
      if (dropped) {
        logger.log("events_dropped_no_consent", { dropped });
      }

      // Persist the exact attempt before yielding to fetch/sendBeacon. A
      // successful sendBeacon call only means the browser accepted the bytes;
      // it is not an HTTP acknowledgement and must remain retryable.
      const stagedSharedRevocationToken = synchronizeSharedConsent();
      const prepared = allowed.map((event): QueuedEvent => {
        const deliveryClass = resolveQueuedDeliveryClass(event)!;
        return {
          ...event,
          delivery:
            event.delivery ??
            buildQueuedDeliveryContext(
              event.type,
              deliveryClass,
              stagedSharedRevocationToken,
            ),
        };
      });
      stageDeliveryAttempt(queue, prepared);

      for (const { delivery, events } of groupEventsByDeliveryContext(
        prepared,
      )) {
        const deliveryClass = delivery.deliveryClass;
        const essential = deliveryClass === "essential";
        const stagedRevocationGeneration =
          deliveryClass === "marketing"
            ? marketingRevocationGeneration
            : analyticsRevocationGeneration;
        const canContinueDelivery = () => {
          const currentSharedRevocationToken = synchronizeSharedConsent();
          return isConsentBoundDeliveryCurrent({
            isEssential: essential,
            currentlyAllowed: canTrack(deliveryClass),
            stagedRevocationGeneration,
            currentRevocationGeneration:
              deliveryClass === "marketing"
                ? marketingRevocationGeneration
                : analyticsRevocationGeneration,
            stagedSharedRevocationToken: delivery.revocationToken,
            currentSharedRevocationToken,
          });
        };
        const apiBase = delivery.apiBase;
        const envelope = {
          siteKey: delivery.siteKey,
          sdk: delivery.sdk,
          consent: delivery.consent,
          policy: delivery.policy,
          ...(delivery.visitor ? { visitor: delivery.visitor } : {}),
          ...(delivery.revocation ? { revocation: delivery.revocation } : {}),
          ...(delivery.context ? { context: delivery.context } : {}),
        };
        const payload = {
          ...envelope,
          sentAt: now(),
          batchId: ulid(),
          events: events.map((event) => ({
            eventId: event.eventId,
            type: event.type,
            occurredAt: event.occurredAt,
            properties: event.props,
          })),
          reason,
        };
        const url = `${apiBase.replace(/\/+$/, "")}/pxl/ingest`;
        const result = useBeacon
          ? canContinueDelivery()
            ? await postJson(url, payload, { useBeacon })
            : { ok: false, cancelled: true }
          : await postWithRetryResult(url, payload, {
              beforeAttempt: canContinueDelivery,
            });
        if (!essential)
          observeTestCaptureResponse(
            result.status,
            delivery.consent.cmp?.testEnrollmentToken,
          );

        if (consentEvidenceKey) {
          acknowledgeConsentEvidenceDelivery({
            currentMarker: currentConsentEvidenceMarker,
            events,
            fingerprintKey: consentEvidenceKey,
            storage: storage.local,
            result,
          });
        }

        settleDeliveryAttempt(queue, events, result, {
          shouldRetain: () =>
            essential ? canTrack("essential") : canContinueDelivery(),
        });
      }
    };

    const flush = async (options: FlushOptions = {}) => {
      if (options.useBeacon) {
        try {
          await flushInternal(options);
        } catch (error) {
          logger.error("flush_beacon_failed", error);
        }
        return;
      }

      if (flushInFlight) {
        flushQueued = {
          reason: options.reason ?? "coalesced",
          useBeacon: Boolean(options.useBeacon || flushQueued?.useBeacon),
        };
        return;
      }

      flushInFlight = true;
      try {
        let nextFlush: FlushOptions | undefined = options;
        while (nextFlush) {
          const currentFlush = nextFlush;
          flushQueued = undefined;
          try {
            await flushInternal(currentFlush);
          } catch (error) {
            logger.error("flush_failed", error);
          }
          nextFlush = flushQueued;
        }
      } finally {
        flushInFlight = false;
      }
    };

    let flushTimer: ReturnType<typeof setInterval> | undefined;
    const queueFlushTimer = () => {
      if (flushTimer) return;
      flushTimer = setInterval(() => {
        void flush({ reason: "interval" });
      }, config.queue.flushIntervalMs);
    };

    let flushHooksInstalled = false;
    const preparePageHide = () => {
      // Plugin snapshots must enter the queue before flush takes its batch.
      // Their existing per-instance and consent fences still gate every write.
      for (const instance of pluginInstances.values()) {
        try {
          instance.onPageHide?.();
        } catch {
          // A plugin must not interrupt the host page or other snapshots.
        }
      }
    };
    const installFlushHooks = () => {
      const doc = safeDocument();
      if (!doc || flushHooksInstalled) return;
      flushHooksInstalled = true;
      doc.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          preparePageHide();
          void flush({ useBeacon: true, reason: "visibility" });
        }
      });
      window.addEventListener("pagehide", () => {
        preparePageHide();
        void flush({ useBeacon: true, reason: "pagehide" });
      });
    };

    const loadPluginScript = (name: string, manifestData: PluginManifest) => {
      if (pluginFactories.has(name)) return Promise.resolve();
      const pending = pendingPluginLoads.get(name);
      if (pending) return pending.promise;

      const plugin = manifestData.plugins[name];
      if (!plugin) return Promise.resolve();

      const basePath =
        typeof manifestData.basePath === "string" &&
        manifestData.version === 2 &&
        manifestData.basePath.startsWith("/cdn/v2/")
          ? manifestData.basePath.replace(/\/+$/, "")
          : "/cdn/v2/plugins";

      const pluginPath = plugin.path;
      const pluginPathPattern = /^[a-z0-9_-]+(?:\/[a-z0-9_-]+)*\.js$/i;
      if (
        typeof pluginPath !== "string" ||
        pluginPath.startsWith("/") ||
        pluginPath.includes("\\\\") ||
        pluginPath.includes("..") ||
        !pluginPathPattern.test(pluginPath)
      ) {
        logger.warn("plugin_path_invalid", name, pluginPath);
        return Promise.resolve();
      }

      const srcUrl = new URL(`${basePath}/${pluginPath}`, scriptUrl.origin);
      if (
        srcUrl.origin !== scriptUrl.origin ||
        !srcUrl.pathname.startsWith("/cdn/v2/plugins/")
      ) {
        logger.warn("plugin_url_blocked", name, srcUrl.toString());
        return Promise.resolve();
      }

      let resolvePromise: () => void = () => {};
      const promise = new Promise<void>((resolve) => {
        resolvePromise = resolve;
      });
      pendingPluginLoads.set(name, { promise, resolve: resolvePromise });

      const script = document.createElement("script");
      script.async = true;
      script.src = srcUrl.toString();
      if (typeof plugin.integrity === "string" && plugin.integrity.trim()) {
        script.integrity = plugin.integrity;
        script.crossOrigin = "anonymous";
      }
      script.onload = () => {
        resolvePromise();
        pendingPluginLoads.delete(name);
      };
      script.onerror = () => {
        logger.warn("plugin_load_failed", name);
        resolvePromise();
        pendingPluginLoads.delete(name);
      };
      document.head.appendChild(script);

      return promise;
    };

    const resolvePluginOrder = (
      names: string[],
      manifestData: PluginManifest,
    ) => {
      const order: string[] = [];
      const visiting = new Set<string>();
      const visited = new Set<string>();

      const visit = (name: string) => {
        if (visited.has(name) || visiting.has(name)) return;
        visiting.add(name);
        const deps = manifestData.plugins[name]?.deps ?? [];
        deps.forEach(visit);
        visiting.delete(name);
        visited.add(name);
        order.push(name);
      };

      names.forEach(visit);
      return order;
    };

    const essentialPlugins = new Set<string>([
      "bot",
      "consent",
      "diagnostics",
      "gcm",
      "identityopportunity",
      "presence",
    ]);

    const resolvePluginTier = (name: string) => {
      if (essentialPlugins.has(name)) return "essential";
      const tiers = config.plugins.tiers ?? {};
      if (tiers.essential?.includes(name)) return "essential";
      if (tiers.early?.includes(name)) return "early";
      if (tiers.late?.includes(name)) return "late";
      if (tiers.idle?.includes(name)) return "idle";
      const manifestTier = manifest.plugins[name]?.tier;
      if (manifestTier) return manifestTier;
      return "early";
    };

    const isMarketingPlugin = (name: string) =>
      config.privacy.marketingPlugins.includes(name) ||
      manifest.plugins[name]?.kind === "marketing";

    const initPlugin = (name: string) => {
      // Script loading may have awaited the network while another tab changed
      // consent. Reconcile the shared fence immediately before any factory or
      // plugin side effect.
      synchronizeSharedConsent();
      if (flags.killSwitch) return;
      const factory = pluginFactories.get(name);
      if (!factory) return;
      if (pluginInstances.has(name)) return;
      const pluginDeliveryClass = resolvePluginTransportKind({
        isMarketing: isMarketingPlugin(name),
        isTrustedEssential: essentialPlugins.has(name),
      });
      if (!canTrack(pluginDeliveryClass)) return;
      if (
        shouldUseAnonymousAnalyticsFallback(
          pluginDeliveryClass,
          identifierCreationAllowed(),
        ) &&
        !isAnonymousAnalyticsFallbackPlugin(name)
      ) {
        return;
      }
      const activity = createPluginActivityFence();
      const pluginTrack = (type: string, props?: Record<string, unknown>) => {
        if (!activity.isActive()) return;
        trackEvent(type, props, pluginDeliveryClass);
      };
      const pluginPostWithRetry = (
        url: string,
        payload: unknown,
        options?: { beforeAttempt?: () => boolean },
      ) => {
        // Scheduling tiers are remotely configurable and must never grant a
        // consent exemption. Marketing classification wins; only hard-coded
        // privacy/control plugins are trusted as essential.
        const kind = pluginDeliveryClass;
        const stagedRevocationGeneration =
          kind === "marketing"
            ? marketingRevocationGeneration
            : analyticsRevocationGeneration;
        const stagedSharedRevocationToken = synchronizeSharedConsent();
        return postWithRetry(url, payload, {
          beforeAttempt: () => {
            if (!activity.isActive()) return false;
            const currentSharedRevocationToken = synchronizeSharedConsent();
            if (flags.killSwitch) return false;
            if (!canTrack(kind)) return false;
            if (options?.beforeAttempt && !options.beforeAttempt()) {
              return false;
            }
            if (kind === "essential") return true;
            const currentRevocationGeneration =
              kind === "marketing"
                ? marketingRevocationGeneration
                : analyticsRevocationGeneration;
            return isConsentBoundDeliveryCurrent({
              isEssential: false,
              currentlyAllowed: true,
              stagedRevocationGeneration,
              currentRevocationGeneration,
              stagedSharedRevocationToken,
              currentSharedRevocationToken,
            });
          },
        });
      };
      const ctx = {
        config,
        flags,
        siteKey: config.siteKey,
        get ids() {
          return activity.isActive() ? getPolicyBoundIds() : undefined;
        },
        get lastPageView() {
          const revocationToken = synchronizeSharedConsent();
          return activity.isActive() &&
            canTrackCategory("session_quality") &&
            identifierCreationAllowed() &&
            lastPageView?.siteKey === config.siteKey &&
            lastPageView.revocationToken === revocationToken
            ? { ...lastPageView.observation }
            : undefined;
        },
        clearIds: clearBrowserIds,
        get consent() {
          return consentSnapshot;
        },
        reconcileConsent: (
          snapshot: ConsentSnapshot,
          transition?: FirstPartyConsentTransition,
        ) =>
          activity.isActive()
            ? setConsent(snapshot, true, "cmp", true, transition)
            : Promise.resolve(consentSnapshot),
        firstPartyCookie: {
          issue: () =>
            activity.isActive()
              ? firstPartyCookieCoordinator.issue(
                  { apiBase: config.apiBase, siteKey: config.siteKey },
                  () => {
                    const url = `${config.apiBase.replace(/\/+$/, "")}/pxl/firstparty`;
                    return issueFirstPartyCookie({
                      url,
                      buildCurrentRequest:
                        buildCurrentFirstPartyCookieIssueRequest,
                      isAllowed: firstPartyCookieIssueAllowed,
                      post: pluginPostWithRetry,
                    });
                  },
                )
              : Promise.resolve(false),
          revoke: (options?: {
            clearBrowserIds?: boolean;
            cookieOnly?: boolean;
          }) => revokeServerFirstPartyCookie(options),
        },
        LOG: logger.log,
        warn: logger.warn,
        error: logger.error,
        track: pluginTrack,
        queue: { track: pluginTrack },
        emit: (event: string, payload: unknown) => {
          if (activity.isActive()) bus.emit(event as any, payload as any);
        },
        on: (event: string, handler: (payload: unknown) => void) =>
          bus.on(event as any, (payload: unknown) => {
            if (activity.isActive()) handler(payload);
          }),
        transport: { postWithRetry: pluginPostWithRetry },
        redact: redactor,
        storage: storage.local,
        canTrack: (kind?: "essential" | "marketing" | "analytics") =>
          activity.isActive() && canTrack(kind),
        canTrackCategory: (category: DataCategory) =>
          activity.isActive() && canTrackCategory(category),
        identityCaptureReadiness: () =>
          activity.isActive()
            ? identityCaptureReadiness()
            : ({ ready: false, reason: "workspace_policy" } as const),
        captureIdentity: (capture: FirstPartyIdentityCapture) =>
          activity.isActive()
            ? captureIdentity(capture)
            : Promise.resolve(false),
      };
      const instance = factory(ctx) ?? {};
      pluginInstanceDeactivators.set(instance, activity.deactivate);
      pluginInstances.set(name, instance);
      try {
        instance.onInit?.();
      } catch {
        // ignore
      }
    };

    const resolveEnabledPlugins = (
      tiers: Set<"essential" | "early" | "late" | "idle">,
    ) => {
      const disabled = new Set([
        ...config.plugins.disabled,
        ...(flags.disabledPlugins ?? []),
      ]);

      const isMarketingBlocked = !canTrack("marketing");
      const requested = new Set<string>([
        ...manifest.defaults,
        ...(config.plugins.enabled ?? []),
      ]);

      const candidates = Array.from(requested)
        .filter((name) => !disabled.has(name))
        .filter((name) => Boolean(manifest.plugins[name]))
        .filter((name) => name !== "diagnostics" || config.debug);

      const filtered = isMarketingBlocked
        ? candidates.filter((name) => !isMarketingPlugin(name))
        : candidates;

      return filtered.filter((name) => tiers.has(resolvePluginTier(name)));
    };

    const unloadNonEssentialPlugins = () => {
      const keep = new Set(essentialPlugins);
      if (config.debug) keep.add("diagnostics");
      for (const [name, instance] of Array.from(pluginInstances.entries())) {
        if (keep.has(name)) continue;
        pluginInstanceDeactivators.get(instance)?.();
        try {
          instance.onUnload?.();
        } catch {
          // ignore
        }
        pluginInstances.delete(name);
      }
    };

    const unloadMarketingPluginInstances = () => {
      for (const [name, instance] of Array.from(pluginInstances.entries())) {
        if (!isMarketingPlugin(name)) continue;
        pluginInstanceDeactivators.get(instance)?.();
        try {
          instance.onUnload?.();
        } catch {
          // ignore
        }
        pluginInstances.delete(name);
      }
    };

    const loadedTiers = new Set<string>();
    handleAnalyticsConsentRevoked = () => {
      lastPageView = undefined;
      unloadNonEssentialPlugins();
      loadedTiers.forEach((tier) => {
        if (tier !== "essential") loadedTiers.delete(tier);
      });
    };
    handleMarketingConsentRevoked = () => {
      unloadMarketingPluginInstances();
      loadedTiers.forEach((tier) => {
        if (tier !== "essential") loadedTiers.delete(tier);
      });
    };
    const loadPluginsInternal = async (
      tiers: Set<"essential" | "early" | "late" | "idle">,
    ) => {
      const optedOut = hasDurableOptout(storage.local);
      // Bootstrap may apply a host choice before config/probes finish. Do not
      // consume plugin tiers while initPlugin is still forbidden to start them.
      // Initialization schedules them again after opening the bootstrap gate.
      if (privacyBootstrapPending) return;
      const next = Array.from(tiers).filter((tier) => {
        if (tier !== "essential" && !canTrack("analytics")) return false;
        if (!loadedTiers.has(tier)) return true;
        // A tier can run while cookie verification is pending, when only
        // anonymous-safe plugins are eligible. Retry its still-missing plugins
        // after the real probe settles; initPlugin enforces every gate again.
        return resolveEnabledPlugins(new Set([tier])).some((name) => {
          if (pluginInstances.has(name)) return false;
          const kind = resolvePluginTransportKind({
            isMarketing: isMarketingPlugin(name),
            isTrustedEssential: essentialPlugins.has(name),
          });
          return (
            canTrack(kind) &&
            (!shouldUseAnonymousAnalyticsFallback(
              kind,
              identifierCreationAllowed(),
            ) ||
              isAnonymousAnalyticsFallbackPlugin(name))
          );
        });
      });
      if (!next.length) return;

      // An opt-out stops capture, but must not remove the visitor's privacy
      // controls on the next page. Only the consent UI may initialize here.
      const enabled = resolveEnabledPlugins(new Set(next)).filter(
        (name) => !optedOut || name === "consent",
      );
      const order = resolvePluginOrder(enabled, manifest);
      const readyQueue: string[] = [];

      for (const name of order) {
        const hadInstance = pluginInstances.has(name);
        await loadPluginScript(name, manifest);
        initPlugin(name);
        if (!hadInstance && pluginInstances.has(name)) {
          readyQueue.push(name);
        }
      }

      readyQueue.forEach((name) => {
        try {
          pluginInstances.get(name)?.onReady?.();
        } catch {
          // ignore
        }
      });

      next.forEach((tier) => loadedTiers.add(tier));
    };

    let pluginWorkQueue: Promise<void> = Promise.resolve();
    const enqueuePluginWork = (work: () => Promise<void> | void) => {
      pluginWorkQueue = pluginWorkQueue.then(async () => {
        try {
          await work();
        } catch {
          // ignore
        }
      });
      return pluginWorkQueue;
    };

    const loadPlugins = (tiers: Set<"essential" | "early" | "late" | "idle">) =>
      enqueuePluginWork(() => loadPluginsInternal(tiers));

    handleMarketingConsentGranted = () => {
      loadedTiers.forEach((tier) => {
        if (tier !== "essential") loadedTiers.delete(tier);
      });
      void loadPlugins(new Set(["early", "late", "idle"] as const));
    };
    handleAnalyticsConsentGranted = () => {
      loadedTiers.forEach((tier) => {
        if (tier !== "essential") loadedTiers.delete(tier);
      });
      void loadPlugins(new Set(["early", "late", "idle"] as const));
    };

    const unloadPlugins = () =>
      enqueuePluginWork(() => {
        unloadNonEssentialPlugins();
        loadedTiers.forEach((tier) => {
          if (tier !== "essential") loadedTiers.delete(tier);
        });
      });

    const queuedConsentSnapshot = (
      payload: QueuedConsentPayload,
    ): ConsentSnapshot => {
      const cmp =
        payload.cmp ??
        (payload.tcString ||
        payload.uspString ||
        payload.gppString ||
        payload.gppSectionIds
          ? {
              source: payload.tcString
                ? "tcf"
                : payload.gppString
                  ? "gpp"
                  : payload.uspString
                    ? "usp"
                    : "manual",
              tcString: payload.tcString,
              uspString: payload.uspString,
              gppString: payload.gppString,
              gppSectionIds: payload.gppSectionIds,
            }
          : undefined);
      return {
        state: payload.state,
        gpc: consentSnapshot.gpc,
        dnt: consentSnapshot.dnt,
        cmp,
      };
    };

    let initialized = false;
    let initializationSuppressedByOptout = false;
    let stopEnquiryCapture: (() => void) | undefined;
    let initPromise: Promise<void> | undefined;
    let queuedPrivacyBootstrap:
      | ReturnType<typeof resolveQueuedPrivacyBootstrap>
      | undefined;
    const init = (override?: Partial<PixelConfig>) => {
      if (initialized) return Promise.resolve();
      if (initPromise) return initPromise;
      enquiryAttributionGeneration += 1;
      initPromise = (async () => {
        // No rich or linkable work may escape while the current cookie
        // partition's transport/fence status is unresolved. After bootstrap,
        // an affirmative analytics decision may use only the fixed coarse,
        // identifier-free aggregate fallback.
        privacyBootstrapPending = true;
        config = resolveConfig(scriptUrl, override);
        config.consentMode = normalizeConsentMode(config.consentMode);
        bindConsentStorageScope(config.siteKey);
        logger.log("pixel_init", {
          siteKey: config.siteKey,
          apiBase: config.apiBase,
        });

        const [defaults, fetchedFlags, fetchedManifest] = await Promise.all([
          loadDefaults(scriptUrl),
          loadFlags(scriptUrl),
          loadManifest(scriptUrl),
        ]);

        config = resolveConfig(
          scriptUrl,
          deepMerge(defaults as PixelConfig, override ?? {}),
        );
        config.consentMode = normalizeConsentMode(config.consentMode);
        bindConsentStorageScope(config.siteKey);
        if (config.features) {
          config = applyFeatureFlags(config, config.features);
        }
        flags = fetchedFlags;
        manifest = fetchedManifest;
        redactor = createRedactor({
          redactKeys: config.privacy.redactKeys,
          allowedQueryParams: config.privacy.allowedQueryParams,
        });

        const remoteConfig = await loadRemoteConfig(
          config.apiBase,
          config.siteKey,
        );
        if (remoteConfig) {
          const remoteOverrides: Record<string, unknown> = {};
          if (remoteConfig.features) {
            remoteOverrides.features = remoteConfig.features;
          }
          if (remoteConfig.platform) {
            remoteOverrides.platform = remoteConfig.platform;
          }
          if (remoteConfig.plugins) {
            remoteOverrides.plugins = remoteConfig.plugins;
          }
          if (remoteConfig.destinations) {
            remoteOverrides.destinations = remoteConfig.destinations;
          }
          if (remoteConfig.consentMode) {
            remoteOverrides.consentMode = remoteConfig.consentMode;
          }
          if (remoteConfig.privacy) {
            remoteOverrides.privacy = remoteConfig.privacy;
          }
          if (remoteConfig.identityCapture) {
            remoteOverrides.identityCapture = remoteConfig.identityCapture;
          }
          if (remoteConfig.intelligence) {
            remoteOverrides.intelligence = remoteConfig.intelligence;
          }
          if (Object.keys(remoteOverrides).length > 0) {
            config = resolveConfig(
              scriptUrl,
              deepMerge(config as PixelConfig, remoteOverrides),
            );
            config.consentMode = normalizeConsentMode(config.consentMode);
          }
        }
        if (testInvitation) config.privacy.consentEnforcement = "required";
        if (config.features) {
          config = applyFeatureFlags(config, config.features);
        }
        stopEnquiryCapture?.();
        stopEnquiryCapture = config.siteKey
          ? startEnquiryCapture(win, {
              apiBase: config.apiBase,
              siteKey: config.siteKey,
              prepareAttribution: (form, submitter) =>
                prepareEnquiryAttribution({
                  snapshot: () => {
                    const sharedRevocationToken = synchronizeSharedConsent();
                    if (
                      !canTransmitIdentity() ||
                      privacyBootstrapPending ||
                      resolveIdentityFormPolicyBlockReason(
                        config.identityCapture,
                        form,
                        submitter,
                        win.location,
                      ) ||
                      !config.identityCapture.purposes.includes(
                        "identity_resolution",
                      )
                    )
                      return undefined;
                    const ids = idManager.getExistingIds();
                    if (!ids) return undefined;
                    return {
                      apiBase: config.apiBase,
                      siteKey: config.siteKey,
                      anonId: ids.anonId,
                      sessionId: ids.sessionId,
                      consentGeneration: consentTransitionGeneration,
                      identityRevocationGeneration,
                      firstPartyFenceGeneration:
                        firstPartyConsentFenceGeneration(),
                      configurationGeneration: enquiryAttributionGeneration,
                      sharedRevocationToken,
                    };
                  },
                  context: (snapshot) => ({
                    occurredAt: now(),
                    visitor: {
                      anonId: snapshot.anonId,
                      sessionId: snapshot.sessionId,
                    },
                    consent: consentSnapshot,
                  }),
                }),
            })
          : undefined;
        queue = createQueue(storage.local, {
          key: `prospix_queue_${config.siteKey || "default"}`,
          maxEvents: config.queue.maxEvents,
          maxBytes: config.queue.maxBytes,
          ttlMs: config.queue.ttlMs,
        });

        document.documentElement.dataset.prospixConsentEnforcement =
          config.privacy.consentEnforcement ?? "required";
        window.dispatchEvent(
          new CustomEvent("prospix:consent-policy", {
            detail: {
              enforcement: config.privacy.consentEnforcement ?? "required",
            },
          }),
        );

        if (
          hasDurableOptout(storage.local) ||
          queuedPrivacyBootstrap?.optout === true
        ) {
          await setConsent(
            {
              state: "denied",
              gpc: consentSnapshot.gpc,
              dnt: consentSnapshot.dnt,
            },
            true,
            "optout_recovery",
          );
          retainOnlyEssentialQueuedEvents(queue, isPrivacyEssentialQueuedEvent);
          privacyBootstrapPending = false;
          void revokeServerFirstPartyCookie();
          await flush({ reason: "optout_recovery" });
          initializationSuppressedByOptout = true;
          initialized = true;
          queueFlushTimer();
          installFlushHooks();
          await loadPlugins(new Set(["essential"] as const));
          return;
        }

        const queuedCookieTargets = new Map<
          string,
          { apiBase: string; siteKey: string }
        >();
        for (const event of queue.peek()) {
          const target = queuedFirstPartyCookieTarget(event);
          if (target) {
            queuedCookieTargets.set(
              queuedFirstPartyCookieTargetKey(target),
              target,
            );
          }
        }
        const currentCookieTarget = {
          apiBase: config.apiBase,
          siteKey: config.siteKey,
        };
        let firstPartyProbePromise: Promise<boolean> | undefined;
        if (currentCookieTarget.siteKey) {
          queuedCookieTargets.set(
            queuedFirstPartyCookieTargetKey(currentCookieTarget),
            currentCookieTarget,
          );
          if (!flags.killSwitch) {
            // Start in parallel, but never delay applying an initial browser or
            // CMP restriction. Identity, marketing, and rich analytics remain
            // gated until this settles.
            firstPartyProbePromise =
              ensureFirstPartyCookieTransportProbe(currentCookieTarget);
          }
        }

        const identityCookieProhibited =
          flags.killSwitch || resolveCategoryMode("identity_linkage") === "off";
        const cookieOnlyDisabled =
          config.features?.firstPartyCookies === false ||
          !firstPartyCookiePluginEnabled();
        if (identityCookieProhibited || cookieOnlyDisabled) {
          if (identityCookieProhibited) {
            clearBrowserIds();
            // A full identity-policy revoke invalidates older envelopes. A
            // cookie-feature-only disable does not: those records remain safe
            // after their captured origins acknowledge cookie expiry.
            queue.remove(
              queue
                .peek()
                .filter(
                  (event) => event.delivery?.policy.firstPartyCookies !== false,
                )
                .map((event) => event.eventId),
            );
          }
          for (const target of queuedCookieTargets.values()) {
            scheduleQueuedFirstPartyCookieRevoke(
              target,
              false,
              cookieOnlyDisabled || !isCurrentFirstPartyTarget(target),
            );
          }
        }
        if (
          queuedPrivacyBootstrap?.consent &&
          config.privacy.consentEnforcement !== "preview"
        ) {
          const queuedConsent = queuedPrivacyBootstrap.consent;
          const queuedSnapshot = queuedConsentSnapshot(queuedConsent);
          // The queued command is an input to the initial decision, not an
          // override of a newer browser/CMP read. Resolve live signals last so
          // Shopify, TCF, and GPC denial cannot be overwritten by a stale
          // loader grant.
          const resolved = await resolveConsent(config, {
            state: queuedSnapshot.state,
            updatedAt: now(),
          });
          await setConsent(
            {
              ...resolved,
              cmp: resolved.cmp ?? queuedSnapshot.cmp,
            },
            true,
            resolved.cmp ? "cmp" : "manual",
            true,
            // Preserve the original click evidence when the host supplied the
            // initial choice. Restored preferences have no transition, and a
            // live CMP decision must never inherit the host click's authority.
            resolved.cmp
              ? undefined
              : {
                  transitionVersion: queuedConsent.transitionVersion,
                  occurredAt: queuedConsent.occurredAt,
                },
          );
        } else {
          await refreshConsent();
        }
        queueFlushTimer();
        installFlushHooks();
        if (firstPartyProbePromise && !(await firstPartyProbePromise)) {
          logger.warn("firstparty_cookie_transport_unverified");
        }
        privacyBootstrapPending = false;
        const purposeFencedSnapshot = applyFirstPartyPurposeFence(
          consentSnapshot,
          currentCookieTarget,
        );
        if (
          !sharedConsentSnapshotsEqual(
            purposeFencedSnapshot,
            consentSnapshot,
          ) &&
          (isConsentDeliveryAllowed({
            snapshot: consentSnapshot,
            kind: "analytics",
            consentMode: resolveConsentMode(),
            respectGpc: config.privacy.respectGpc,
            respectDnt: config.privacy.respectDnt,
          }) ||
            isConsentDeliveryAllowed({
              snapshot: consentSnapshot,
              kind: "marketing",
              consentMode: resolveConsentMode(),
              respectGpc: config.privacy.respectGpc,
              respectDnt: config.privacy.respectDnt,
            }))
        ) {
          await setConsent(purposeFencedSnapshot, true, "firstparty_receipt");
        }
        const loadEssential = () =>
          loadPlugins(new Set(["essential"] as const));
        let nonEssentialScheduled = false;
        const scheduleNonEssentialLoads = () => {
          if (!canTrack("analytics")) return;
          const doc = safeDocument();
          const loadEarly = () => loadPlugins(new Set(["early"] as const));
          const loadLate = () => loadPlugins(new Set(["late"] as const));
          const loadIdle = () => loadPlugins(new Set(["idle"] as const));

          if (doc && doc.readyState === "loading") {
            if (!nonEssentialScheduled) {
              doc.addEventListener("DOMContentLoaded", loadEarly, {
                once: true,
              });
            }
          } else {
            loadEarly();
          }

          if (doc && doc.readyState === "complete") {
            loadLate();
          } else if (!nonEssentialScheduled) {
            window.addEventListener("load", loadLate, { once: true });
          }

          const idle = (window as any).requestIdleCallback as
            | ((cb: () => void, opts?: { timeout: number }) => void)
            | undefined;
          if (typeof idle === "function") {
            idle(loadIdle, { timeout: 3000 });
          } else {
            setTimeout(loadIdle, 3000);
          }
          nonEssentialScheduled = true;
        };

        void loadEssential();
        scheduleNonEssentialLoads();

        // Consent-driven plugin gating for opt-in/strict modes.
        bus.on("consent", () => {
          if (storage.local.get("prospix_optout") === "1") return;
          void captureCompanyVisit();
          if (canTrack("analytics")) {
            scheduleNonEssentialLoads();
          } else {
            void unloadPlugins();
          }
        });
        initializationSuppressedByOptout = false;
        initialized = true;
        void captureCompanyVisit();
      })().catch((error) => {
        initPromise = undefined;
        throw error;
      });
      return initPromise;
    };

    const api = (win as any).prospix || {};
    const queueCalls: unknown[] = Array.isArray(api.q) ? api.q : [];

    api.q = queueCalls;
    api.init = (override?: Partial<PixelConfig>) => init(override);
    api.track = (type: string, props?: Record<string, unknown>) =>
      trackEvent(type, props, "analytics");
    api.captureIdentity = async (capture: FirstPartyIdentityCapture) => {
      await init();
      return captureIdentity(capture);
    };
    api.confirmEnquiry = async (proof: string) => {
      if (typeof proof !== "string" || !proof || proof.length > 2048)
        return false;
      await init();
      return captureIdentity({
        source: "confirmed_form",
        purposes: ["identity_resolution"],
        enquiryProof: proof,
      });
    };
    api.identify = async (traits: Record<string, unknown> = {}) => {
      await init();
      const stagedSharedRevocationToken = synchronizeSharedConsent();
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("identity_linkage")) return;
      const stagedIdentityRevocationGeneration = identityRevocationGeneration;
      const stagedMarketingRevocationGeneration = marketingRevocationGeneration;
      const rawEmail = readIdentityRecordValue(traits, "email");
      const rawPhone = readIdentityRecordValue(traits, "phone");
      const rawExternalId = readIdentityRecordValue(traits, "externalId");
      const rawProfile = {
        fullName: readIdentityRecordValue(traits, "fullName"),
        company: readIdentityRecordValue(traits, "company"),
        jobTitle: readIdentityRecordValue(traits, "jobTitle"),
        website: readIdentityRecordValue(traits, "website"),
        first: readIdentityRecordValue(traits, "first"),
        middle: readIdentityRecordValue(traits, "middle"),
        last: readIdentityRecordValue(traits, "last"),
        street: readIdentityRecordValue(traits, "street"),
        city: readIdentityRecordValue(traits, "city"),
        state: readIdentityRecordValue(traits, "state"),
        postalCode: readIdentityRecordValue(traits, "postalCode"),
        country: readIdentityRecordValue(traits, "country"),
      };
      const hasProtectedIdentityInput = Boolean(
        rawEmail ||
        rawPhone ||
        rawExternalId ||
        Object.values(rawProfile).some(Boolean),
      );
      const protectedIdentityCaptured = await captureIdentity({
        source: "identify_api",
        email: rawEmail,
        phone: rawPhone,
        externalId: rawExternalId,
        profile: rawProfile,
      });
      if (hasProtectedIdentityInput && !protectedIdentityCaptured) return;
      const identifyOperationIsCurrent = () => {
        const currentSharedRevocationToken = synchronizeSharedConsent();
        return isConsentBoundDeliveryCurrent({
          isEssential: false,
          currentlyAllowed: hasProtectedIdentityInput
            ? identityConsentAllows()
            : canTrack("marketing") && canTrackCategory("identity_linkage"),
          stagedRevocationGeneration: hasProtectedIdentityInput
            ? stagedIdentityRevocationGeneration
            : stagedMarketingRevocationGeneration,
          currentRevocationGeneration: hasProtectedIdentityInput
            ? identityRevocationGeneration
            : marketingRevocationGeneration,
          stagedSharedRevocationToken,
          currentSharedRevocationToken,
        });
      };
      if (!identifyOperationIsCurrent()) return;
      const allowlist = new Set(
        config.privacy.identifyAllowlist.map((key) => key.toLowerCase()),
      );
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(traits)) {
        if (isProtectedIdentityTraitKey(key)) continue;
        if (allowlist.has(key.toLowerCase())) {
          out[key] = redactor.sanitizeValue(value);
        }
      }
      const hasCrypto =
        typeof crypto !== "undefined" &&
        crypto.subtle &&
        typeof TextEncoder !== "undefined";
      if (hasCrypto && rawEmail) {
        const normalized = rawEmail.trim().toLowerCase();
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(normalized),
        );
        if (!identifyOperationIsCurrent()) return;
        out.email_hash = Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      }
      if (hasCrypto && rawPhone) {
        const normalized = rawPhone.replace(/[^0-9+]/g, "");
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(normalized),
        );
        if (!identifyOperationIsCurrent()) return;
        out.phone_hash = Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      }
      if (hasCrypto && rawExternalId) {
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(rawExternalId.trim()),
        );
        if (!identifyOperationIsCurrent()) return;
        out.external_id_hash = Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      }
      if (identifyOperationIsCurrent()) {
        trackEvent("identify", out, "marketing");
      }
    };
    api.resolveIdentity = async (match: IdentityResolutionInput) => {
      await init();
      if (config.features?.identityResolution === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("identity_resolution")) return;
      const normalized = normalizeIdentityResolution(match);
      if (!normalized) return;
      if (!canAcceptProvider(config, normalized.provider)) return;
      trackEvent("identity_resolution", normalized, "marketing");
    };
    api.recordDecision = async (decision: DecisionInput) => {
      await init();
      if (config.features?.decisionTrace === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("audience_decisioning")) return;
      const normalized = normalizeDecision(decision);
      if (!normalized) return;
      if (
        normalized.provider &&
        !canAcceptProvider(config, normalized.provider)
      ) {
        return;
      }
      trackEvent("decision_recorded", normalized, "marketing");
    };
    api.recordExposure = async (exposure: ExposureInput) => {
      await init();
      if (config.features?.decisionTrace === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("audience_decisioning")) return;
      const normalized = normalizeExposure(exposure);
      if (!normalized) return;
      trackEvent("decision_exposure", normalized, "marketing");
    };
    api.recordOutcome = async (outcome: OutcomeInput) => {
      await init();
      if (config.features?.decisionTrace === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("audience_decisioning")) return;
      const normalized = normalizeOutcome(outcome);
      if (!normalized) return;
      if (
        normalized.provider &&
        !canAcceptProvider(config, normalized.provider)
      ) {
        return;
      }
      trackEvent("decision_outcome", normalized, "marketing");
    };
    api.consent = (payload: QueuedConsentPayload) => {
      if (isRestoredHostChoiceDuringTest(consentSnapshot, payload)) return;
      // Preview controls do not write a visitor choice or trigger revocation.
      // Explicit optout() and browser privacy signals retain their own paths.
      if (config.privacy.consentEnforcement === "preview") return;
      const next = queuedConsentSnapshot(payload);
      if (!initialized) {
        // Host banners can call the published API while init is awaiting config
        // or the first-party probe. Include that choice in bootstrap, then replay
        // the original transition after init so a default snapshot cannot erase
        // it. Preserve existing opt-out/fence evidence and the click timestamp.
        queueCalls.push(["consent", payload]);
        queuedPrivacyBootstrap = {
          ...(queuedPrivacyBootstrap ?? resolveQueuedPrivacyBootstrap([])),
          consent: payload,
        };
        privacyBootstrapPending = true;
        // Restrictions still apply synchronously to the consent lifecycle; do
        // not wait on a potentially stalled configuration request to revoke.
        const restrictsExistingPurpose = (
          ["analytics", "marketing"] as const
        ).some(
          (purpose) =>
            isConsentAuthorizedForSnapshot(consentSnapshot, purpose) &&
            !isConsentAuthorizedForSnapshot(next, purpose),
        );
        if (next.state === "granted" && !restrictsExistingPurpose) return;
      }
      return setConsent(next, true, "manual", true, {
        transitionVersion: payload.transitionVersion,
        occurredAt: payload.occurredAt,
      });
    };
    api.recordConsentResponse = (choice: unknown) => {
      if (!initialized) {
        queueCalls.push(["recordConsentResponse", choice]);
        return;
      }
      recordConsentResponse(
        {
          config,
          siteKey: config.siteKey,
          storage: storage.local,
          canTrackCategory,
          track: (type, props) => {
            trackEvent(type, props, "essential");
          },
        },
        choice,
        "marketing_banner_v1",
      );
    };
    api.openConsent = () => {
      window.dispatchEvent(new CustomEvent("prospix:open-consent"));
    };
    let optoutWorkQueue = Promise.resolve();
    api.optout = (enabled: boolean) => {
      const transition = enabled
        ? ({ transitionVersion: 1, occurredAt: now() } as const)
        : undefined;
      if (enabled) {
        // This must happen in the caller's stack, before the serialized async
        // lifecycle starts, so navigation or a hanging revoke cannot preserve
        // a stale local grant on the next page.
        markDurableOptout(storage.local);
      }
      optoutWorkQueue = optoutWorkQueue
        .catch(() => {})
        .then(async () => {
          if (enabled) {
            try {
              await setConsent(
                {
                  state: "denied",
                  gpc: consentSnapshot.gpc,
                  dnt: consentSnapshot.dnt,
                },
                true,
                "optout",
                true,
                transition,
              );
            } catch (error) {
              logger.error("optout_consent_failed", error);
            }
            // Stop local nonessential collection and remove browser identifiers
            // before waiting on either network acknowledgement path.
            retainOnlyEssentialQueuedEvents(
              queue,
              isPrivacyEssentialQueuedEvent,
            );
            clearBrowserIds();
            await unloadPlugins();
            void revokeServerFirstPartyCookie();
            await flush({ reason: "optout" });
            return;
          }

          clearDurableOptout(storage.local);
          if (initializationSuppressedByOptout) {
            initialized = false;
            initPromise = undefined;
            initializationSuppressedByOptout = false;
            await init();
          } else if (canTrack("analytics")) {
            await loadPlugins(new Set(["early", "late", "idle"] as const));
          }
        });
      return optoutWorkQueue;
    };
    api.flush = (options?: { reason?: string }) =>
      flush({ useBeacon: false, reason: options?.reason ?? "manual" });
    api.getIds = () => getPolicyBoundIds();
    let testExpiryTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleTestExpiry = (expiresAt: number) => {
      if (testExpiryTimer) clearTimeout(testExpiryTimer);
      testExpiryTimer = setTimeout(
        () => {
          void api.stopWebsiteTest().catch(() => {
            const status = document
              .getElementById("prospix-test-enrollment")
              ?.shadowRoot?.querySelector('[role="status"]');
            if (status)
              status.textContent =
                "Capture has stopped locally. Connect to confirm withdrawal.";
          });
        },
        Math.max(0, expiresAt - now()),
      );
    };
    api.stopWebsiteTest = async () => {
      const cmp = consentSnapshot.cmp;
      if (testExpiryTimer) clearTimeout(testExpiryTimer);
      const localStop = api.optout(true).then(() => {
        const panel = document.getElementById(
          "prospix-test-enrollment",
        )?.shadowRoot;
        const title = panel?.querySelector("h2");
        const text = panel?.querySelector("p");
        if (title) title.textContent = "Website test stopped";
        if (text)
          text.textContent =
            "Activity and submitted-contact capture have stopped in this browser.";
      });
      let withdrawal: Promise<unknown> = Promise.resolve();
      if (cmp?.testEnrollmentToken && cmp.testEnrollmentBrowserId)
        withdrawal = fetch(
          new URL("/pxl/test-enrollment/withdraw", config.apiBase).toString(),
          {
            method: "POST",
            credentials: "include",
            keepalive: true,
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              siteKey: config.siteKey,
              browserId: cmp.testEnrollmentBrowserId,
              sessionToken: cmp.testEnrollmentToken,
            }),
          },
        ).then((response) => {
          if (!response.ok) throw new Error("test_withdrawal_unconfirmed");
        });
      await Promise.all([localStop, withdrawal]);
      document.getElementById("prospix-test-enrollment")?.remove();
    };
    api.joinWebsiteTest = async () => {
      await init();
      const invitation = testInvitation;
      if (
        !invitation ||
        hasDurableOptout(storage.local) ||
        consentSnapshot.gpc ||
        consentSnapshot.dnt ||
        (navigator as Navigator & { globalPrivacyControl?: boolean })
          .globalPrivacyControl === true ||
        ["1", "yes"].includes(navigator.doNotTrack ?? "")
      )
        throw new Error("test_privacy_restriction");
      const ids = getIds();
      const response = await fetch(
        new URL("/pxl/test-enrollment", config.apiBase).toString(),
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            siteKey: config.siteKey,
            invitationToken: invitation,
            browserId: ids.anonId,
            acknowledged: true,
            gpc: false,
            dnt: false,
          }),
        },
      );
      if (!response.ok) throw new Error("test_enrollment_rejected");
      const accepted = await response.json();
      if (
        invitation !== testInvitation ||
        typeof accepted.sessionToken !== "string" ||
        typeof accepted.expiresAt !== "number" ||
        accepted.expiresAt <= now()
      )
        throw new Error("test_enrollment_rejected");
      config.destinations = { enabled: [], config: {}, eventMaps: {} };
      const next: ConsentSnapshot = {
        state: "granted",
        gpc: false,
        dnt: false,
        cmp: {
          source: TEST_ENROLLMENT_SOURCE,
          purposes: { version: 1, analytics: true, marketing: false },
          testEnrollmentToken: accepted.sessionToken,
          testEnrollmentBrowserId: ids.anonId,
          testEnrollmentExpiresAt: accepted.expiresAt,
        },
      };
      const livePermission = await resolveConsent(config, {
        ...next,
        updatedAt: now(),
      });
      if (!isActiveTestEnrollment(livePermission))
        throw new Error("test_privacy_restriction");
      // Publish the answer-only banner policy before the consent event causes
      // its renderer to refresh. Host and browser restrictions still use the
      // ordinary reconciliation/withdrawal paths.
      config.privacy.consentEnforcement = "preview";
      document.documentElement.dataset.prospixConsentEnforcement = "preview";
      await setConsent(livePermission, true, "manual", true, {
        transitionVersion: 1,
        occurredAt: now(),
      });
      if (!isActiveTestEnrollment(consentSnapshot)) {
        config.privacy.consentEnforcement = "required";
        document.documentElement.dataset.prospixConsentEnforcement = "required";
        window.dispatchEvent(
          new CustomEvent("prospix:consent-policy", {
            detail: { enforcement: "required" },
          }),
        );
        throw new Error("test_permission_unconfirmed");
      }
      window.dispatchEvent(
        new CustomEvent("prospix:consent-policy", {
          detail: { enforcement: "preview" },
        }),
      );
      scheduleTestExpiry(accepted.expiresAt);
      return accepted.expiresAt as number;
    };
    const showTestEnrollment = () => {
      if (isActiveTestEnrollment(consentSnapshot)) {
        config.destinations = { enabled: [], config: {}, eventMaps: {} };
        scheduleTestExpiry(consentSnapshot.cmp!.testEnrollmentExpiresAt!);
      }
      mountTestEnrollmentPrompt({
        invitation: testInvitation,
        expiresAt: isActiveTestEnrollment(consentSnapshot)
          ? consentSnapshot.cmp?.testEnrollmentExpiresAt
          : undefined,
        captureStatus: testCaptureStatus,
        join: api.joinWebsiteTest,
        stop: () => api.stopWebsiteTest(),
      });
    };
    const onTestInvitationChange = () => {
      const invitation = takeTestInvitation(win);
      if (!invitation) return;
      testInvitation = invitation;
      if (isActiveTestEnrollment(consentSnapshot)) return;
      document.getElementById("prospix-test-enrollment")?.remove();
      if (initialized) showTestEnrollment();
    };
    window.addEventListener("hashchange", onTestInvitationChange);
    window.addEventListener(
      "unload",
      () => {
        window.removeEventListener("hashchange", onTestInvitationChange);
      },
      { once: true },
    );

    api.getPlatformStatus = async () => {
      await init();
      return getPlatformStatus(config);
    };

    (win as any).prospix = api;

    // Drain calls queued by the loader shim.
    const processQueue = async () => {
      const initialCalls = queueCalls.splice(0);
      const initOverride = resolveQueuedInitOverride(initialCalls);
      if (
        initOverride &&
        typeof initOverride === "object" &&
        !Array.isArray(initOverride)
      ) {
        // Bind the final init-only site before any queued denial can persist,
        // rotate a fence, or target a cookie revoke. Consent is never read
        // from a cross-site `default` bucket.
        config = resolveConfig(scriptUrl, initOverride as Partial<PixelConfig>);
        config.consentMode = normalizeConsentMode(config.consentMode);
        bindConsentStorageScope(config.siteKey);
        queue = createQueue(storage.local, {
          key: `prospix_queue_${config.siteKey || "default"}`,
          maxEvents: config.queue.maxEvents,
          maxBytes: config.queue.maxBytes,
          ttlMs: config.queue.ttlMs,
        });
      }
      // The host can mount before runtime policy is published. Do not turn its
      // older saved banner preference into a fresh withdrawal of a joined test.
      const bootstrapCalls = initialCalls.filter((entry) => {
        const method = Array.isArray(entry) ? entry[0] : (entry as any)?.method;
        const payload = Array.isArray(entry)
          ? entry[1]
          : (entry as any)?.args?.[0];
        const ignored =
          method === "consent" &&
          payload &&
          isRestoredHostChoiceDuringTest(consentSnapshot, payload);
        if (
          ignored &&
          !Array.isArray(entry) &&
          typeof (entry as any)?.resolve === "function"
        ) {
          (entry as any).resolve(undefined);
        }
        return !ignored;
      });
      const privacyBootstrap = resolveQueuedPrivacyBootstrap(bootstrapCalls);
      queuedPrivacyBootstrap = privacyBootstrap;
      if (privacyBootstrap.revocationOccurred) {
        // Persist the denial and shared revocation fence before init's first
        // network await. This protects sibling tabs and the next navigation,
        // even if defaults/config fetches hang or the page unloads.
        const queuedDenial = privacyBootstrap.revocationConsent;
        const restoredChoiceSupersededByFreshGrant =
          queuedDenial &&
          !isFreshFirstPartyConsentTransition(queuedDenial) &&
          privacyBootstrap.consent?.state === "granted" &&
          isFreshFirstPartyConsentTransition(privacyBootstrap.consent);
        // A restored host preference is not a new denial. Re-minting it after
        // the user's queued click would produce a later server fence that the
        // original click cannot resolve. Bootstrap/probes keep delivery gated.
        if (!restoredChoiceSupersededByFreshGrant)
          void setConsent(
            queuedDenial?.state === "denied"
              ? queuedConsentSnapshot(queuedDenial)
              : {
                  state: "denied",
                  gpc: consentSnapshot.gpc,
                  dnt: consentSnapshot.dnt,
                },
            true,
            privacyBootstrap.optout === true ? "optout" : "manual",
            true,
            queuedDenial
              ? {
                  transitionVersion: queuedDenial.transitionVersion,
                  occurredAt: queuedDenial.occurredAt,
                }
              : undefined,
          );
      }
      if (privacyBootstrap.optout === true) {
        markDurableOptout(storage.local);
      } else if (privacyBootstrap.optout === false) {
        clearDurableOptout(storage.local);
      }
      if (
        privacyBootstrap.consent !== undefined ||
        privacyBootstrap.optout !== undefined
      ) {
        // Close the loader-to-runtime gap before init performs its first await.
        privacyBootstrapPending = true;
      }
      try {
        await init(initOverride as Partial<PixelConfig> | undefined);
      } catch (error) {
        queueCalls.unshift(...initialCalls);
        throw error;
      }

      const replayCalls = [
        ...bootstrapCalls.map((entry, index) => ({
          entry,
          bootstrapConsumed: privacyBootstrap.consumedIndexes.has(index),
        })),
        ...queueCalls.splice(0).map((entry) => ({
          entry,
          bootstrapConsumed: false,
        })),
      ];
      for (const { entry, bootstrapConsumed } of replayCalls) {
        if (bootstrapConsumed) {
          if (
            entry &&
            typeof entry === "object" &&
            !Array.isArray(entry) &&
            typeof (entry as any).resolve === "function"
          ) {
            (entry as any).resolve(undefined);
          }
          continue;
        }
        if (Array.isArray(entry)) {
          const [method, ...args] = entry;
          if (method === "init") continue;
          if (typeof (api as any)[method] === "function") {
            await (api as any)[method](...args);
          }
          continue;
        }
        if (entry && typeof entry === "object" && "method" in (entry as any)) {
          const method = (entry as any).method;
          const args = (entry as any).args ?? [];
          if (method === "init") continue;
          if (typeof (api as any)[method] === "function") {
            try {
              const result = await (api as any)[method](...args);
              if (typeof (entry as any).resolve === "function") {
                (entry as any).resolve(result);
              }
            } catch (error) {
              if (typeof (entry as any).reject === "function") {
                (entry as any).reject(error);
                continue;
              }
              throw error;
            }
          }
        }
      }
      showTestEnrollment();
      api.q = [];
      api.ready = true;
      window.dispatchEvent(new Event("prospix:ready"));
    };

    void processQueue().catch((error) =>
      logger.error("pixel_init_failed", error),
    );
  } catch {
    // swallow errors
  }
})();
